import React, { useState, useEffect, useContext, useRef } from 'react';
import * as Sentry from '@sentry/react';
import briefcase from '../assets/briefcase.svg';
import schoolbook from '../assets/schoolbook.svg';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import axios from 'axios';
import { UserAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Skills from './Skills';
import { useUserContext } from '../context/UserContext';
import ProfileContext from '../context/ProfileContext';
import { useStepContext } from '../context/StepContext';
import moment from 'moment';
import { CommonUniversities } from '../components/CommonUniversities';
import { CommonBoards10th, CommonBoards12th } from '../components/CommonBoards';
import { DatePickerWithMonthName, normalizeDateInputValue } from '../components/DatePickerWithMonthName';
import { computeFreezeStatus, FROZEN_FORM_KEYS } from '../constants/frozenFields';

import PlacesAutocomplete, {
    geocodeByAddress,
} from 'react-places-autocomplete';
import { Button, Row, Col, Modal } from 'react-bootstrap';

const SAL_SCALE_MULTIPLIERS = { Thousands: 1000, Lakhs: 100000, Crore: 10000000 };
const parseExpToYearsMonths = (val) => {
    const parts = String(val || '0').split('.');
    return { years: parts[0] || '0', months: parts[1] || '0' };
};

const experienceToMonths = (years, months) => {
    if (years === '' || months === '') return null;
    const y = parseInt(years, 10);
    const m = parseInt(months, 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return null;
    return y * 12 + m;
};

const RequiredMark = () => <span className="text-danger ms-1">*</span>;
const OptionalMark = () => <span className="text-muted fw-normal ms-1">(optional)</span>;

const WORK_STATUS_VALUES = ['In Job', 'Currently On Notice', 'Unemployed', 'Fresher'];

const getTotalVsRelevantExpError = (totalYears, totalMonths, relYears, relMonths) => {
    const total = experienceToMonths(totalYears, totalMonths);
    if (total === null) return '';
    const rel = experienceToMonths(relYears === '' ? '0' : relYears, relMonths === '' ? '0' : relMonths);
    if (rel === null) return '';
    if (total < rel) {
        return 'Total experience must be greater than relevant experience.';
    }
    return '';
};

// Given a raw numeric salary (e.g. 333000), returns { value: '333', scale: 'Thousands' }
const parseAnnualSalary = (raw) => {
    if (!raw) return { value: '', scale: '' };
    const str = String(raw).toLowerCase();

    // Handle "6.0 Lakhs" or "3.5 Crore" format
    if (str.includes('lakh')) {
        const val = str.replace(/[^\d.]/g, '');
        return { value: val, scale: 'Lakhs' };
    }
    if (str.includes('crore')) {
        const val = str.replace(/[^\d.]/g, '');
        return { value: val, scale: 'Crore' };
    }
    if (str.includes('thousand')) {
        const val = str.replace(/[^\d.]/g, '');
        return { value: val, scale: 'Thousands' };
    }

    const num = parseInt(str.replace(/\D/g, ''), 10);
    if (!num || isNaN(num)) return { value: '', scale: '' };
    if (num % 10000000 === 0 && num / 10000000 <= 99) return { value: String(num / 10000000), scale: 'Crore' };
    if (num % 100000 === 0 && num / 100000 <= 999) return { value: String(num / 100000), scale: 'Lakhs' };
    if (num % 1000 === 0 && num / 1000 <= 999) return { value: String(num / 1000), scale: 'Thousands' };
    return { value: String(num), scale: '' };
};

const SkillsVal = Skills.map((skill, index) => ({
    id: index + 1,
    name: skill,
    value: skill,
    label: skill
}));

const Course = [
    // Graduation
    { id: 1, name: "B.S", label: "B.S", value: "B.S", category: "graduation" },
    { id: 2, name: "B.E", label: "B.E", value: "B.E", category: "graduation" },
    { id: 3, name: "B.Tech", label: "B.Tech", value: "B.Tech", category: "graduation" },
    { id: 4, name: "B.IT", label: "B.IT", value: "B.IT", category: "graduation" },
    { id: 7, name: "BCA", label: "BCA", value: "BCA", category: "graduation" },
    { id: 9, name: "A.A.S", label: "A.A.S", value: "A.A.S", category: "graduation" },
    { id: 10, name: "B.A", label: "B.A", value: "B.A", category: "graduation" },
    { id: 11, name: "B.Com", label: "B.Com", value: "B.Com", category: "graduation" },
    { id: 12, name: "B.Sc", label: "B.Sc", value: "B.Sc", category: "graduation" },
    { id: 17, name: "B.Ed", label: "B.Ed", value: "B.Ed", category: "graduation" },
    { id: 19, name: "LLB", label: "LLB", value: "LLB", category: "graduation" },
    { id: 21, name: "MBBS", label: "MBBS", value: "MBBS", category: "graduation" },
    { id: 22, name: "BDS", label: "BDS", value: "BDS", category: "graduation" },
    { id: 23, name: "B.Pharm", label: "B.Pharm", value: "B.Pharm", category: "graduation" },
    { id: 28, name: "B.Arch", label: "B.Arch", value: "B.Arch", category: "graduation" },
    { id: 29, name: "B.Design", label: "B.Design", value: "B.Design", category: "graduation" },
    // Masters
    { id: 5, name: "M.Eng", label: "M.Eng", value: "M.Eng", category: "masters" },
    { id: 6, name: "MCA", label: "MCA", value: "MCA", category: "masters" },
    { id: 13, name: "M.A", label: "M.A", value: "M.A", category: "masters" },
    { id: 14, name: "M.Com", label: "M.Com", value: "M.Com", category: "masters" },
    { id: 15, name: "M.Sc", label: "M.Sc", value: "M.Sc", category: "masters" },
    { id: 16, name: "MBA", label: "MBA", value: "MBA", category: "masters" },
    { id: 18, name: "M.Ed", label: "M.Ed", value: "M.Ed", category: "masters" },
    { id: 20, name: "LLM", label: "LLM", value: "LLM", category: "masters" },
    { id: 24, name: "M.Pharm", label: "M.Pharm", value: "M.Pharm", category: "masters" },
    { id: 30, name: "M.Tech", label: "M.Tech", value: "M.Tech", category: "masters" },
    { id: 31, name: "M.Des", label: "M.Des", value: "M.Des", category: "masters" },
    // Ph.D / Doctorate
    { id: 8, name: "Ph.D", label: "Ph.D", value: "Ph.D", category: "phd" },
    { id: 32, name: "D.Sc", label: "D.Sc", value: "D.Sc", category: "phd" },
    { id: 33, name: "D.Litt", label: "D.Litt", value: "D.Litt", category: "phd" },
    { id: 34, name: "D.Phil", label: "D.Phil", value: "D.Phil", category: "phd" },
    { id: 35, name: "Post-Doctoral", label: "Post-Doctoral", value: "Post-Doctoral", category: "phd" },
    // Diploma
    { id: 25, name: "Diploma in Engineering", label: "Diploma in Engineering", value: "Diploma in Engineering", category: "diploma" },
    { id: 36, name: "Diploma in Computer Science", label: "Diploma in Computer Science", value: "Diploma in Computer Science", category: "diploma" },
    { id: 37, name: "Diploma in Business Management", label: "Diploma in Business Management", value: "Diploma in Business Management", category: "diploma" },
    { id: 38, name: "Diploma in Hotel Management", label: "Diploma in Hotel Management", value: "Diploma in Hotel Management", category: "diploma" },
    { id: 39, name: "Diploma in Fashion Design", label: "Diploma in Fashion Design", value: "Diploma in Fashion Design", category: "diploma" },
    { id: 40, name: "Diploma in Pharmacy", label: "Diploma in Pharmacy", value: "Diploma in Pharmacy", category: "diploma" },
    { id: 41, name: "Diploma in Nursing", label: "Diploma in Nursing", value: "Diploma in Nursing", category: "diploma" },
    { id: 42, name: "Diploma in Electrical Engineering", label: "Diploma in Electrical Engineering", value: "Diploma in Electrical Engineering", category: "diploma" },
    { id: 43, name: "Diploma in Civil Engineering", label: "Diploma in Civil Engineering", value: "Diploma in Civil Engineering", category: "diploma" },
    { id: 44, name: "Diploma in Mechanical Engineering", label: "Diploma in Mechanical Engineering", value: "Diploma in Mechanical Engineering", category: "diploma" },
    // Intermediate (12th)
    { id: 26, name: "12th (Science)", label: "12th (Science)", value: "12th (Science)", category: "12th" },
    { id: 45, name: "12th (Commerce)", label: "12th (Commerce)", value: "12th (Commerce)", category: "12th" },
    { id: 46, name: "12th (Arts)", label: "12th (Arts)", value: "12th (Arts)", category: "12th" },
    // Highschool (10th)
    { id: 27, name: "10th", label: "10th", value: "10th", category: "10th" },
]

const Specialization = [
    {
        id: 1,
        name: "Computers",
        label: "Computers",
        value: "Computers",
    },
    {
        id: 2,
        name: "Finance",
        label: "Finance",
        value: "Finance",
    },
    {
        id: 3,
        name: "IT Networks",
        label: "IT Networks",
        value: "IT Networks",
    },
    {
        id: 4,
        name: "Advertising Management",
        label: "Advertising Management",
        value: "Advertising Management",
    },
    {
        id: 5,
        name: "Mass Communication",
        label: "Mass Communication",
        value: "Mass Communication",
    },
    {
        id: 6,
        name: "Media Management",
        label: "Media Management",
        value: "Media Management",
    },
    {
        id: 7,
        name: "Computer Science",
        label: "Computer Science",
        value: "Computer Science",
    },
    {
        id: 8,
        name: "Information Technology",
        label: "Information Technology",
        value: "Information Technology",
    },
    {
        id: 9,
        name: "Software Engineering",
        label: "Software Engineering",
        value: "Software Engineering",
    },
    {
        id: 10,
        name: "Data Science",
        label: "Data Science",
        value: "Data Science",
    },
    {
        id: 11,
        name: "Artificial Intelligence",
        label: "Artificial Intelligence",
        value: "Artificial Intelligence",
    },
    {
        id: 12,
        name: "Cybersecurity",
        label: "Cybersecurity",
        value: "Cybersecurity",
    },
    {
        id: 13,
        name: "Mechanical Engineering",
        label: "Mechanical Engineering",
        value: "Mechanical Engineering",
    },
    {
        id: 14,
        name: "Electrical Engineering",
        label: "Electrical Engineering",
        value: "Electrical Engineering",
    },
    {
        id: 15,
        name: "Electronics & Communication",
        label: "Electronics & Communication",
        value: "Electronics & Communication",
    },
    {
        id: 16,
        name: "Civil Engineering",
        label: "Civil Engineering",
        value: "Civil Engineering",
    },
    {
        id: 17,
        name: "Chemical Engineering",
        label: "Chemical Engineering",
        value: "Chemical Engineering",
    },
    {
        id: 18,
        name: "Accounting",
        label: "Accounting",
        value: "Accounting",
    },
    {
        id: 19,
        name: "Marketing",
        label: "Marketing",
        value: "Marketing",
    },
    {
        id: 20,
        name: "Human Resources",
        label: "Human Resources",
        value: "Human Resources",
    },
    {
        id: 21,
        name: "Operations Management",
        label: "Operations Management",
        value: "Operations Management",
    },
    {
        id: 22,
        name: "Business Administration",
        label: "Business Administration",
        value: "Business Administration",
    },
    {
        id: 23,
        name: "Economics",
        label: "Economics",
        value: "Economics",
    },
    {
        id: 24,
        name: "Banking",
        label: "Banking",
        value: "Banking",
    },
    {
        id: 25,
        name: "English Literature",
        label: "English Literature",
        value: "English Literature",
    },
    {
        id: 26,
        name: "Psychology",
        label: "Psychology",
        value: "Psychology",
    },
    {
        id: 27,
        name: "Sociology",
        label: "Sociology",
        value: "Sociology",
    },
    {
        id: 28,
        name: "Political Science",
        label: "Political Science",
        value: "Political Science",
    },
    {
        id: 29,
        name: "History",
        label: "History",
        value: "History",
    },
    {
        id: 30,
        name: "Geography",
        label: "Geography",
        value: "Geography",
    },
    {
        id: 31,
        name: "Physics",
        label: "Physics",
        value: "Physics",
    },
    {
        id: 32,
        name: "Chemistry",
        label: "Chemistry",
        value: "Chemistry",
    },
    {
        id: 33,
        name: "Mathematics",
        label: "Mathematics",
        value: "Mathematics",
    },
    {
        id: 34,
        name: "Biology",
        label: "Biology",
        value: "Biology",
    },
    {
        id: 35,
        name: "Biotechnology",
        label: "Biotechnology",
        value: "Biotechnology",
    },
    {
        id: 36,
        name: "Microbiology",
        label: "Microbiology",
        value: "Microbiology",
    },
    {
        id: 37,
        name: "Pharmacy",
        label: "Pharmacy",
        value: "Pharmacy",
    },
    {
        id: 38,
        name: "Medicine",
        label: "Medicine",
        value: "Medicine",
    },
    {
        id: 39,
        name: "Dentistry",
        label: "Dentistry",
        value: "Dentistry",
    },
    {
        id: 40,
        name: "Nursing",
        label: "Nursing",
        value: "Nursing",
    },
    {
        id: 41,
        name: "Law",
        label: "Law",
        value: "Law",
    },
    {
        id: 42,
        name: "Journalism",
        label: "Journalism",
        value: "Journalism",
    },
    {
        id: 43,
        name: "Architecture",
        label: "Architecture",
        value: "Architecture",
    },
    {
        id: 44,
        name: "Fashion Design",
        label: "Fashion Design",
        value: "Fashion Design",
    },
    {
        id: 45,
        name: "Graphic Design",
        label: "Graphic Design",
        value: "Graphic Design",
    },
    {
        id: 46,
        name: "Interior Design",
        label: "Interior Design",
        value: "Interior Design",
    },
    {
        id: 47,
        name: "Hotel Management",
        label: "Hotel Management",
        value: "Hotel Management",
    },
    {
        id: 48,
        name: "Tourism",
        label: "Tourism",
        value: "Tourism",
    },
    {
        id: 49,
        name: "Agriculture",
        label: "Agriculture",
        value: "Agriculture",
    },
    {
        id: 50,
        name: "Environmental Science",
        label: "Environmental Science",
        value: "Environmental Science",
    },
    {
        id: 52,
        name: "PCM",
        label: "PCM (Physics, Chemistry, Math)",
        value: "PCM",
    },
    {
        id: 53,
        name: "PCB",
        label: "PCB (Physics, Chemistry, Biology)",
        value: "PCB",
    },
    {
        id: 54,
        name: "PCMB",
        label: "PCMB (Physics, Chemistry, Math, Biology)",
        value: "PCMB",
    },
    {
        id: 55,
        name: "Commerce with Math",
        label: "Commerce with Math",
        value: "Commerce with Math",
    },
    {
        id: 56,
        name: "Commerce without Math",
        label: "Commerce without Math",
        value: "Commerce without Math",
    },
    {
        id: 57,
        name: "Arts / Humanities",
        label: "Arts / Humanities",
        value: "Arts / Humanities",
    },
    {
        id: 58,
        name: "General",
        label: "General",
        value: "General",
    },
    {
        id: 51,
        name: "Other",
        label: "Other",
        value: "Other",
    },
]

const OTHER_OPTION = { name: 'Other', label: 'Other', value: 'Other' };

const SPECIALIZATION_MAP = {
    'B.Tech': ['Computer Science', 'Information Technology', 'Software Engineering', 'Mechanical Engineering', 'Electrical Engineering', 'Electronics & Communication', 'Civil Engineering', 'Chemical Engineering', 'Data Science', 'Artificial Intelligence', 'Cybersecurity', 'Other'],
    'B.E': ['Computer Science', 'Information Technology', 'Mechanical Engineering', 'Electrical Engineering', 'Electronics & Communication', 'Civil Engineering', 'Chemical Engineering', 'Other'],
    'B.S': ['Computer Science', 'Information Technology', 'Physics', 'Chemistry', 'Mathematics', 'Biology', 'Data Science', 'Other'],
    'M.Tech': ['Computer Science', 'Information Technology', 'Software Engineering', 'Mechanical Engineering', 'Electrical Engineering', 'Electronics & Communication', 'Civil Engineering', 'Data Science', 'Artificial Intelligence', 'Cybersecurity', 'Other'],
    'M.Eng': ['Mechanical Engineering', 'Electrical Engineering', 'Electronics & Communication', 'Civil Engineering', 'Chemical Engineering', 'Computer Science', 'Other'],
    'BCA': ['Computer Science', 'Information Technology', 'Software Engineering', 'IT Networks', 'Cybersecurity', 'Other'],
    'MCA': ['Computer Science', 'Information Technology', 'Software Engineering', 'Data Science', 'Artificial Intelligence', 'IT Networks', 'Cybersecurity', 'Other'],
    'B.IT': ['Computer Science', 'Information Technology', 'Software Engineering', 'IT Networks', 'Other'],
    'A.A.S': ['Computer Science', 'Information Technology', 'Other'],
    'B.Com': ['Finance', 'Accounting', 'Economics', 'Banking', 'Marketing', 'Business Administration', 'Other'],
    'M.Com': ['Finance', 'Accounting', 'Economics', 'Banking', 'Marketing', 'Business Administration', 'Operations Management', 'Other'],
    'MBA': ['Finance', 'Marketing', 'Human Resources', 'Operations Management', 'Business Administration', 'Economics', 'Accounting', 'Banking', 'Advertising Management', 'Mass Communication', 'Other'],
    'B.A': ['English Literature', 'Psychology', 'Sociology', 'Political Science', 'History', 'Geography', 'Journalism', 'Mass Communication', 'Media Management', 'Other'],
    'M.A': ['English Literature', 'Psychology', 'Sociology', 'Political Science', 'History', 'Geography', 'Journalism', 'Mass Communication', 'Other'],
    'B.Sc': ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Biotechnology', 'Microbiology', 'Computer Science', 'Environmental Science', 'Agriculture', 'Other'],
    'M.Sc': ['Physics', 'Chemistry', 'Mathematics', 'Biology', 'Biotechnology', 'Microbiology', 'Computer Science', 'Data Science', 'Environmental Science', 'Other'],
    'MBBS': ['Medicine', 'Other'],
    'BDS': ['Dentistry', 'Other'],
    'B.Pharm': ['Pharmacy', 'Other'],
    'M.Pharm': ['Pharmacy', 'Other'],
    'LLB': ['Law', 'Other'],
    'LLM': ['Law', 'Other'],
    'B.Ed': ['Other'],
    'M.Ed': ['Other'],
    'B.Arch': ['Architecture', 'Other'],
    'B.Design': ['Fashion Design', 'Graphic Design', 'Interior Design', 'Other'],
    'M.Des': ['Fashion Design', 'Graphic Design', 'Interior Design', 'Architecture', 'Other'],
    'Diploma in Engineering': ['Mechanical Engineering', 'Electrical Engineering', 'Civil Engineering', 'Electronics & Communication', 'Chemical Engineering', 'Other'],
    'Diploma in Computer Science': ['Computer Science', 'Information Technology', 'Software Engineering', 'IT Networks', 'Other'],
    'Diploma in Business Management': ['Business Administration', 'Marketing', 'Human Resources', 'Finance', 'Other'],
    'Diploma in Hotel Management': ['Hotel Management', 'Tourism', 'Other'],
    'Diploma in Fashion Design': ['Fashion Design', 'Graphic Design', 'Other'],
    'Diploma in Pharmacy': ['Pharmacy', 'Other'],
    'Diploma in Nursing': ['Nursing', 'Other'],
    'Diploma in Electrical Engineering': ['Electrical Engineering', 'Electronics & Communication', 'Other'],
    'Diploma in Civil Engineering': ['Civil Engineering', 'Other'],
    'Diploma in Mechanical Engineering': ['Mechanical Engineering', 'Other'],
    '12th (Science)': ['PCM', 'PCB', 'PCMB', 'Other'],
    '12th (Commerce)': ['Commerce with Math', 'Commerce without Math', 'Other'],
    '12th (Arts)': ['Arts / Humanities', 'Other'],
    '10th': ['General', 'Other'],
};

const getSpecializationsForCourse = (courseValue) => {
    if (!courseValue) return Specialization;
    const allowed = SPECIALIZATION_MAP[courseValue];
    if (!allowed) return Specialization;
    return Specialization.filter((s) => allowed.includes(s.value));
};

const appendOtherCourseOption = (courses) => {
    if (courses.some((c) => c.value === 'Other')) return courses;
    return [...courses, OTHER_OPTION];
};

export const CompleteProfile = ({ nextStep, currentStep, setCurrentStep }) => {
    const { userData, handleUserChange } = useUserContext();
    const { profile } = useContext(ProfileContext);
    const { setIsCompleted } = useStepContext();
    const { user } = UserAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isRevalidationMode = location.state?.revalidationMode === true
        || sessionStorage.getItem('revalidationMode') === 'true';
    const isEditMode = location.state?.editMode === true
        || sessionStorage.getItem('editMode') === 'true';
    // isReviewMode covers both revalidation and edit for shared UI (pre-fill, Next buttons, headings)
    const isReviewMode = isRevalidationMode || isEditMode;
    const isSchedulingRef = useRef(false);
    const hasScheduledInterview = useRef(false);
    const [initialEmploymentState, setInitialEmploymentState] = useState(null);
    const initialEducationRef = useRef(null);
    const initialHeadlineRef = useRef(null);
    const pendingHeadlineMatchRef = useRef('');
    // Form states
    const [address, setAddress] = useState('');
    const [jobtitle, setJobTitle] = useState('');
    const [city, setCity] = useState('');
    const [annualsal, setAnnualsal] = useState('');
    const [annualsal_scale, setAnnualsalScale] = useState('');
    const [selectedDateFrom, setSelectedDateFrom] = useState('');
    const [selectedDateTo, setSelectedDateTo] = useState('');
    const [noticePeriod, setNoticePeriod] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [qualification, setQualification] = useState('');
    const [isQualificationSelected, setIsQualificationSelected] = useState(false);
    const [selectedCourses, setSelectedCourses] = useState(null);
    const [isCourseSelected, setIsCourseSelected] = useState(false);
    const [otherCourse, setOtherCourse] = useState('');
    const [selectedSpecializations, setSelectedSpecializations] = useState(null);
    const [areSpecializationsSelected, setAreSpecializationsSelected] = useState(false);
    const [otherSpecialization, setOtherSpecialization] = useState('');
    const [university, setUniversity] = useState('');
    const [otherUniversity, setOtherUniversity] = useState('');
    const [courseVal, setCourse] = useState('');
    const [isCourseValid, setIsValidCourse] = useState(false);
    const [numberValue, setNumberValue] = useState('');
    const [passingYear, setPassingYear] = useState('');
    const [yearError, setYearError] = useState('');
    const [isform5Submitted, setForm5Submit] = useState(false);
    const [resumeHeadline, setResumeHeadline] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [selectedHeadlineIndex, setSelectedHeadlineIndex] = useState(0);
    const [validationError, setValidationError] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [dateError, setDateError] = useState('');
    // Extra fields
    const [relevantExp, setRelevantExp] = useState('');
    const [expectedCtc, setExpectedCtc] = useState('');
    const [expectedCtcScale, setExpectedCtcScale] = useState('');
    const [reasonJobChange, setReasonJobChange] = useState('');
    const [candidateQuery, setCandidateQuery] = useState('');
    const [isSalaryNegotiable, setIsSalaryNegotiable] = useState('');
    const [canNoticeBuyout, setCanNoticeBuyout] = useState('');
    const [currentCompany, setCurrentCompany] = useState('');
    const [jobStatus, setJobStatus] = useState('');
    // Derived: fresher if jobStatus is 'Fresher' (case-insensitive), experienced otherwise
    const isFresher = String(jobStatus || '').toLowerCase() === 'fresher';
    const isUnemployed = jobStatus === 'Unemployed' || jobStatus === 'No Where Working';
    const isCompanyRequired = !isFresher;
    const [totalExperience, setTotalExperience] = useState(0);
    const [totalExpYears, setTotalExpYears] = useState('');
    const [totalExpMonths, setTotalExpMonths] = useState('');
    const [relevantExpYears, setRelevantExpYears] = useState('0');
    const [relevantExpMonths, setRelevantExpMonths] = useState('0');
    const [relevantExpError, setRelevantExpError] = useState('');

    // HR Freeze state
    const [isHrFrozen, setIsHrFrozen] = useState(false);
    // Headline modal state
    const [showHeadlineModal, setShowHeadlineModal] = useState(false);
    // Track if values came from backend to handle "freeze" logic
    const [backendValues, setBackendValues] = useState({
        jobtitle: false,
        currentCompany: false,
        city: false,
        jobStatus: false,
        totalExperience: false,
        annualsal: false,
        noticePeriod: false,
        relevantExp: false,
        expectedCtc: false,
        reasonJobChange: false,
        candidateQuery: false,
        isSalaryNegotiable: false,
        canNoticeBuyout: false,
        skills: false
    });
    const hasFetched = useRef(false);

    useEffect(() => {
        if (!user?.uid || hasFetched.current) return;

        // Set synchronously to prevent race conditions during async calls
        hasFetched.current = true;

        fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid])

    // scheduleDateTime
    const [currentTime, setCurrentTime] = useState('');
    const [minDate, setMinDate] = useState('');

    const isFieldDisabled = (fieldName) => {
        // When HR-frozen, freeze the specific fields from HR_FROZEN_FIELDS
        if (isHrFrozen) {
            const hrFrozenFormKeys = [
                'jobtitle', 'currentCompany', 'jobStatus', 'totalExperience', 'annualsal', 'noticePeriod',
                // New fields added to freeze scope
                'relevantExp', 'expectedCtc', 'reasonJobChange', 'isSalaryNegotiable', 'canNoticeBuyout', 'candidateQuery',
            ];
            if (hrFrozenFormKeys.includes(fieldName)) return true;
        }
        // annualsal (when not frozen), and noticePeriod (when not frozen) are always editable — never freeze them.
        if (
            !isHrFrozen &&
            (fieldName === "annualsal" ||
                fieldName === "noticePeriod" ||
                fieldName === "totalExperience")
        )
            return false;
        // If field had a value from backend AND we are NOT in edit/revalidation mode, freeze it.
        return backendValues[fieldName] && !isReviewMode;
    };

    useEffect(() => {
        const now = new Date();
        now.setDate(now.getDate() + 0);
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const formattedDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        setMinDate(formattedDateTime);
    }, []);
    useEffect(() => {
        const now = new Date();
        const time = now.toTimeString().slice(0, 8);
        setCurrentTime(time);
    }, []);
    // scheduleDateTime
    const sentryUserEmailRef = useRef('Unknown Email');
    useEffect(() => {
        if (user?.email) {
            sentryUserEmailRef.current = user.email;
            try {
                Sentry.setUser({ email: user.email });
            } catch (_) { /* Sentry unavailable */ }
        }
    }, [user?.email]);

    const logFieldToSentry = (fieldName, value) => {
        try {
            Sentry.captureMessage(`User filled field: ${fieldName} (User: ${sentryUserEmailRef.current?.replace('@', ' at ')})`, {
                level: 'info',
                extra: { value, email: sentryUserEmailRef.current }
            });
        } catch (_) { /* Sentry unavailable */ }
    };

    const logSentryException = (error, extra = {}) => {
        try {
            Sentry.captureException(error, { extra: { email: user?.email, ...extra } });
        } catch (_) { /* Sentry unavailable */ }
    };

    useEffect(() => {
        const handleFocusOut = (e) => {
            if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
                const fieldName = e.target.name || e.target.placeholder || e.target.id || 'unknown_field';
                const isOtpField = String(e.target.name || '').startsWith('ssn-') || /otp/i.test(String(fieldName)) || /otp/i.test(String(e.target.id || ''));
                const isFileInput = e.target.type === 'file';
                let logValue = isOtpField ? '[REDACTED]' : e.target.value;
                if (isFileInput && e.target.files?.[0]) {
                    logValue = { fileName: e.target.files[0].name, fileSize: e.target.files[0].size };
                }
                logFieldToSentry(fieldName, logValue);
            }
        };
        document.addEventListener('focusout', handleFocusOut);
        return () => {
            document.removeEventListener('focusout', handleFocusOut);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Fetch UserInfo
    const fetchUserInfo = async () => {
        try {
            const refreshedToken = await user?.getIdToken();
            const headers = {
                'Authorization': `Bearer ${refreshedToken}`,
                'Content-Type': 'application/json',
            };

            const response = await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
                operation: 'fetchuser',
                email: user?.email,
            }, {
                headers: headers,
            });

            const dataVal = response.data.data[0];
            if (!dataVal) return;

            let info = dataVal?.other_info;
            if (typeof info === 'string') {
                try { info = JSON.parse(info); } catch (e) { info = null; }
            }

            let interview = dataVal?.latest_interview_data;
            if (typeof interview === 'string') {
                try { interview = JSON.parse(interview); } catch (e) { interview = null; }
            }
            // Local object to collect all backend flags
            const newBackendValues = { ...backendValues };
            
            // ✅ Extract schedule_info and compute freeze state via constant
            const sInfo = dataVal?.schedule_info || null;
            let frozen = false;
            const freezeResult = computeFreezeStatus(sInfo);
            if (freezeResult.isFrozen) {
                frozen = true;
                setIsHrFrozen(true);
                // Pre-fill frozen fields from schedule_info
                if (sInfo.current_company) {
                    setCurrentCompany(sInfo.current_company);
                    newBackendValues.currentCompany = true;
                }
                if (sInfo.job_status) {
                    setJobStatus(sInfo.job_status);
                    newBackendValues.jobStatus = true;
                }
                // Prefer relevant_experience; fall back to experience
                const frozenRelExp = sInfo.relevant_experience != null ? sInfo.relevant_experience : sInfo.experience;
                if (frozenRelExp != null) {
                    setRelevantExp(String(frozenRelExp));
                    newBackendValues.relevantExp = true;
                    const relParts = parseExpToYearsMonths(frozenRelExp);
                    setRelevantExpYears(relParts.years);
                    setRelevantExpMonths(relParts.months);
                }
                if (sInfo.experience != null) {
                    setTotalExperience(parseInt(sInfo.experience) || 0);
                    newBackendValues.totalExperience = true;
                    const tepParts = parseExpToYearsMonths(sInfo.experience);
                    setTotalExpYears(tepParts.years);
                    setTotalExpMonths(tepParts.months);
                }
                if (sInfo.current_ctc) {
                    let ctcValue = '', ctcScale = '';
                    try {
                        const decoded = atob(sInfo.current_ctc); // "333 Thousands"
                        const parts = decoded.trim().split(/\s+/);
                        ctcValue = parts[0] || '';
                        ctcScale = parts[1] || '';
                    } catch (e) { /* not base64, fall through */ }
                    // If atob failed or yielded no scale, treat as raw number
                    if (!ctcValue || !ctcScale) {
                        const parsed = parseAnnualSalary(sInfo.current_ctc);
                        ctcValue = parsed.value;
                        ctcScale = parsed.scale;
                    }
                    if (ctcValue) {
                        setAnnualsal(ctcValue);
                        setAnnualsalScale(ctcScale);
                        newBackendValues.annualsal = true;
                    }
                }
                if (sInfo.notice_period) {
                    setNoticePeriod(sInfo.notice_period);
                    newBackendValues.noticePeriod = true;
                }
                if (sInfo.is_job_role_aware) {
                    setJobTitle(sInfo.is_job_role_aware);
                    newBackendValues.jobtitle = true;
                }
                // 6 new frozen fields
                if (sInfo.expected_ctc) {
                    const parsed = parseAnnualSalary(sInfo.expected_ctc);
                    setExpectedCtc(parsed.value);
                    setExpectedCtcScale(parsed.scale);
                    newBackendValues.expectedCtc = true;
                }
                if (sInfo.reason_job_change) {
                    setReasonJobChange(sInfo.reason_job_change);
                    newBackendValues.reasonJobChange = true;
                }
                if (sInfo.is_salary_negotiation) {
                    setIsSalaryNegotiable(String(sInfo.is_salary_negotiation).toLowerCase());
                    newBackendValues.isSalaryNegotiable = true;
                }
                if (sInfo.can_notice_buyout) {
                    setCanNoticeBuyout(String(sInfo.can_notice_buyout).toLowerCase());
                    newBackendValues.canNoticeBuyout = true;
                }
                if (sInfo.candidate_query) {
                    setCandidateQuery(sInfo.candidate_query);
                    newBackendValues.candidateQuery = true;
                }
            }

            // Pre-fill employment fields from candidateInfo (non-frozen path)
            if (!frozen) {
                if (dataVal.current_company) {
                    setCurrentCompany(dataVal.current_company);
                    newBackendValues.currentCompany = true;
                }
                if (dataVal.job_status) {
                    setJobStatus(dataVal.job_status);
                    newBackendValues.jobStatus = true;
                }
                if (dataVal.total_exp) {
                    setTotalExperience(parseInt(dataVal.total_exp) || 0);
                    newBackendValues.totalExperience = true;
                    const tepParts = parseExpToYearsMonths(dataVal.total_exp);
                    setTotalExpYears(tepParts.years);
                    setTotalExpMonths(tepParts.months);
                    setRelevantExp(dataVal.total_exp);
                    newBackendValues.relevantExp = true;
                    const relParts = parseExpToYearsMonths(dataVal.total_exp);
                    setRelevantExpYears(relParts.years);
                    setRelevantExpMonths(relParts.months);
                }
            }

            const isStep6Verified = dataVal?.resume_headline_added === 1;

            const getValue = (obj, keys) => {
                if (!obj) return null;
                for (const key of keys) {
                    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "null" && obj[key] !== "") return obj[key];
                }
                return null;
            };

            const safeDecode = (str) => {
                if (!str || typeof str !== 'string') return str;
                try {
                    if (/^[A-Za-z0-9+/=]+$/.test(str) && str.length >= 4 && str.length % 4 === 0) return atob(str);
                } catch (e) { }
                return str;
            };

            if (info) {
                if (info.working_from) setSelectedDateFrom(normalizeDateInputValue(info.working_from));
                if (info.working_to) setSelectedDateTo(normalizeDateInputValue(info.working_to));

                // Only pre-fill frozen-field values from other_info when NOT frozen
                // (when frozen, schedule_info values take priority and are already set above)
                if (!frozen) {
                    const annSal = getValue(info, ['annual_salary', 'annual_sal', 'annualsal']);
                    if (annSal) {
                        const parsed = parseAnnualSalary(annSal);
                        setAnnualsal(parsed.value);
                        setAnnualsalScale(parsed.scale);
                        newBackendValues.annualsal = true;
                    }

                    const jTitle = getValue(info, ['job_title', 'jobtitle', 'title']);
                    if (jTitle) {
                        setJobTitle(jTitle);
                        newBackendValues.jobtitle = true;
                    }

                    const cComp = getValue(info, ['current_company', 'company', 'current_comp']);
                    if (cComp) {
                        setCurrentCompany(cComp);
                        newBackendValues.currentCompany = true;
                    }

                    const jStat = getValue(info, ['job_status', 'status', 'job_stat']);
                    if (jStat) {
                        setJobStatus(jStat);
                        newBackendValues.jobStatus = true;
                    }

                    const nPer = getValue(info, ['notice_period', 'noticeperiod', 'notice']);
                    if (nPer) {
                        setNoticePeriod(nPer);
                        newBackendValues.noticePeriod = true;
                    }
                }

                // Non-frozen fields — always pre-fill from other_info
                const cCity = getValue(info, ['current_city', 'city', 'location']);
                if (cCity) {
                    setCity(cCity);
                    newBackendValues.city = true;
                }

                const skills = getValue(info, ['key_skill', 'skills', 'key_skills']);
                if (skills) {
                    const skillsArr = String(skills).split(',').map(s => s.trim()).filter(Boolean);
                    setSelectedOption(skillsArr.map(s => ({ value: s, label: s })));
                    newBackendValues.skills = true;
                }

                if (!frozen) {
                    const rExp = getValue(info, ['relevant_experience', 'relevant_exp', 'experience']);
                    if (rExp) {
                        setRelevantExp(rExp);
                        newBackendValues.relevantExp = true;
                        const relParts = parseExpToYearsMonths(rExp);
                        setRelevantExpYears(relParts.years);
                        setRelevantExpMonths(relParts.months);
                    }
                }

                if (!frozen) {
                    const eCtc = getValue(info, ['expected_ctc', 'expected_ctc_amount', 'expectedctc']);
                    if (eCtc) {
                        const parsed = parseAnnualSalary(eCtc);
                        setExpectedCtc(parsed.value);
                        setExpectedCtcScale(parsed.scale);
                        newBackendValues.expectedCtc = true;
                    }

                    const rReason = getValue(info, ['reason_job_change', 'reason_for_change', 'reason']);
                    if (rReason) {
                        setReasonJobChange(rReason);
                        newBackendValues.reasonJobChange = true;
                    }

                    const cQuery = getValue(info, ['candidate_query', 'message', 'query']);
                    if (cQuery) {
                        setCandidateQuery(cQuery);
                        newBackendValues.candidateQuery = true;
                    }

                    const sNeg = getValue(info, ['salaryNegotiation', 'is_salary_negotiation', 'salary_negotiation', 'is_salary_negotiable']);
                    if (sNeg) {
                        setIsSalaryNegotiable(String(sNeg).toLowerCase());
                        newBackendValues.isSalaryNegotiable = true;
                    }

                    const bBuyout = getValue(info, ['is_notice_buyout', 'can_notice_buyout', 'notice_buyout', 'buyout']);
                    if (bBuyout) {
                        setCanNoticeBuyout(String(bBuyout).toLowerCase());
                        newBackendValues.canNoticeBuyout = true;
                    }
                }
            }

            if (interview) {
                const curCtcVal = getValue(interview, ['current_ctc', 'currentctc']);
                if (curCtcVal && !newBackendValues.annualsal) {
                    const decoded = safeDecode(curCtcVal);
                    const parsed = parseAnnualSalary(decoded);
                    setAnnualsal(parsed.value);
                    setAnnualsalScale(parsed.scale);
                    newBackendValues.annualsal = true;
                }

                const expCtcVal = getValue(interview, ['expected_ctc', 'expectedctc']);
                if (expCtcVal && (frozen || !newBackendValues.expectedCtc)) {
                    const decoded = safeDecode(expCtcVal);
                    const parsed = parseAnnualSalary(decoded);
                    setExpectedCtc(parsed.value);
                    setExpectedCtcScale(parsed.scale);
                    newBackendValues.expectedCtc = true;
                }

                const relExpVal = getValue(interview, ['relevant_experience', 'relevant_exp', 'experience']);
                if (relExpVal && (frozen || !newBackendValues.relevantExp)) {
                    setRelevantExp(relExpVal);
                    newBackendValues.relevantExp = true;
                    const relParts = parseExpToYearsMonths(relExpVal);
                    setRelevantExpYears(relParts.years);
                    setRelevantExpMonths(relParts.months);
                }

                const npVal = getValue(interview, ['notice_period', 'noticeperiod']);
                if (npVal && !newBackendValues.noticePeriod) {
                    setNoticePeriod(npVal);
                    newBackendValues.noticePeriod = true;
                }

                const chgReasonVal = getValue(interview, ['reason_job_change', 'reason_for_change', 'reason']);
                if (chgReasonVal && (frozen || !newBackendValues.reasonJobChange)) {
                    setReasonJobChange(chgReasonVal);
                    newBackendValues.reasonJobChange = true;
                }

                const queryVal = getValue(interview, ['candidate_ques', 'candidate_query', 'message', 'query']);
                if (queryVal && (frozen || !newBackendValues.candidateQuery)) {
                    setCandidateQuery(queryVal);
                    newBackendValues.candidateQuery = true;
                }

                const salNegVal = getValue(interview, ['salaryNegotiation', 'is_salary_negotiation', 'salary_negotiation', 'is_salary_negotiable']);
                if (salNegVal && (frozen || !newBackendValues.isSalaryNegotiable)) {
                    const val = String(salNegVal).toLowerCase();
                    setIsSalaryNegotiable(val === 'yes' || val === 'no' ? val : '');
                    newBackendValues.isSalaryNegotiable = true;
                }

                const buyoutVal = getValue(interview, ['is_notice_buyout', 'can_notice_buyout', 'notice_buyout', 'buyout']);
                if (buyoutVal && (frozen || !newBackendValues.canNoticeBuyout)) {
                    const val = String(buyoutVal).toLowerCase();
                    setCanNoticeBuyout(val === 'yes' || val === 'no' ? val : '');
                    newBackendValues.canNoticeBuyout = true;
                }

                const companyVal = getValue(interview, ['current_company', 'company_name', 'company']);
                if (companyVal && !newBackendValues.currentCompany) {
                    setCurrentCompany(companyVal);
                    newBackendValues.currentCompany = true;
                }

                const statusVal = getValue(interview, ['job_status', 'employment_status', 'status']);
                if (statusVal && !newBackendValues.jobStatus) {
                    setJobStatus(statusVal);
                    newBackendValues.jobStatus = true;
                }
            }

            // Final fallback: candidateInfo columns for fields that may have been missed
            // (e.g. frozen candidates where interview_schedule.job_status is null but candidateInfo.job_status is set)
            if (dataVal.job_status && !newBackendValues.jobStatus) {
                setJobStatus(dataVal.job_status);
                newBackendValues.jobStatus = true;
            }
            if (dataVal.current_company && !newBackendValues.currentCompany) {
                setCurrentCompany(dataVal.current_company);
                newBackendValues.currentCompany = true;
            }
            if (dataVal.total_exp != null && !newBackendValues.totalExperience) {
                setTotalExperience(parseInt(dataVal.total_exp) || 0);
                newBackendValues.totalExperience = true;
                const tepParts = parseExpToYearsMonths(dataVal.total_exp);
                setTotalExpYears(tepParts.years);
                setTotalExpMonths(tepParts.months);
            }

            // Apply all backend flags at once
            setBackendValues(newBackendValues);
            setDataLoaded(true);

            // Pre-fill education and headline fields for all modes
            if (info) {
                let courseObj = null;
                let specObj = null;
                let savedOtherCourse = '';
                let savedOtherSpecialization = '';
                if (info.higest_qualification) {
                    setQualification(info.higest_qualification);
                    setIsQualificationSelected(true);
                }
                if (info.course) {
                    const knownCourse = Course.find((c) => c.value === info.course);
                    if (knownCourse) {
                        courseObj = knownCourse;
                    } else {
                        courseObj = OTHER_OPTION;
                        savedOtherCourse = info.course;
                        setOtherCourse(info.course);
                    }
                    setSelectedCourses(courseObj);
                    setIsCourseSelected(true);
                }
                if (info.specialization) {
                    const specVal = String(info.specialization).trim();
                    const courseForSpec = courseObj?.value;
                    const allowedSpecs = getSpecializationsForCourse(courseForSpec);

                    if (courseForSpec === 'MBA' && specVal.includes(',')) {
                        const parts = specVal.split(',').map((s) => s.trim()).filter(Boolean);
                        const objs = [];
                        const customParts = [];
                        parts.forEach((part) => {
                            const known = allowedSpecs.find((s) => s.value === part);
                            if (known) objs.push(known);
                            else customParts.push(part);
                        });
                        if (customParts.length > 0) {
                            objs.push(OTHER_OPTION);
                            savedOtherSpecialization = customParts.join(', ');
                            setOtherSpecialization(savedOtherSpecialization);
                        }
                        specObj = objs;
                        setSelectedSpecializations(objs.length > 0 ? objs : null);
                        setAreSpecializationsSelected(objs.length > 0);
                    } else {
                        const knownSpec = allowedSpecs.find((s) => s.value === specVal);
                        if (knownSpec) {
                            specObj = knownSpec;
                        } else {
                            specObj = OTHER_OPTION;
                            savedOtherSpecialization = specVal;
                            setOtherSpecialization(specVal);
                        }
                        setSelectedSpecializations(specObj);
                        setAreSpecializationsSelected(true);
                    }
                }
                if (info.university) {
                    setUniversity(info.university);
                }
                if (info.course_type) {
                    setCourse(info.course_type);
                    setIsValidCourse(true);
                }
                if (info.start_year) setNumberValue(normalizeDateInputValue(info.start_year));
                if (info.pass_year) setPassingYear(normalizeDateInputValue(info.pass_year));
                const savedResumeHeadline = info.resume_heading ? String(info.resume_heading).trim() : '';
                if (savedResumeHeadline) {
                    setResumeHeadline(savedResumeHeadline);
                    setIsChecked(false);
                    // Fix: generateHeadline() here used stale state (setState is async) — match after fields load.
                    pendingHeadlineMatchRef.current = savedResumeHeadline;
                }

                // ✅ Store initial state for change tracking (review/edit mode only)
                if (isReviewMode) {
                    initialEducationRef.current = {
                        qualification: info.higest_qualification || '',
                        selectedCourses: courseObj,
                        otherCourse: savedOtherCourse,
                        selectedSpecializations: specObj,
                        otherSpecialization: savedOtherSpecialization,
                        university: info.university || '',
                        courseVal: info.course_type || '',
                        numberValue: info.start_year ? normalizeDateInputValue(info.start_year) : '',
                        passingYear: info.pass_year ? normalizeDateInputValue(info.pass_year) : '',
                    };
                    initialHeadlineRef.current = {
                        resumeHeadline: info.resume_heading || ''
                    };
                }
            }

            // Always show step 2
            setCurrentStep(2);
            if (isReviewMode) {
                return;
            }
            if (isStep6Verified) {
                navigate('/download');
                return;
            }

            // Update stepper navigation
            // updateStepperNavigation(isStep1Verified, isStep2Verified, isStep3Verified);

        } catch (error) {
            logSentryException(error);
            console.error(error);
            toast.error('Could not load your profile. Please refresh and try again.');
            setDataLoaded(true);
        }
    };

    // ✅ Generate headline dynamically
    const isCourseOtherSelected = () => selectedCourses?.value === 'Other';

    const isSpecializationOtherSelected = () => {
        if (!selectedSpecializations) return false;
        if (Array.isArray(selectedSpecializations)) {
            return selectedSpecializations.some((s) => s.value === 'Other');
        }
        return selectedSpecializations.value === 'Other';
    };

    const getResolvedCourse = () => (
        isCourseOtherSelected() ? otherCourse.trim() : (selectedCourses?.value || '')
    );

    const getResolvedSpecialization = () => {
        if (!selectedSpecializations) return '';
        if (Array.isArray(selectedSpecializations)) {
            return selectedSpecializations
                .map((s) => (s.value === 'Other' ? otherSpecialization.trim() : s.value))
                .filter(Boolean)
                .join(', ');
        }
        return isSpecializationOtherSelected() ? otherSpecialization.trim() : (selectedSpecializations.value || '');
    };

    const isHrFrozenEducationField = (formKey) => isHrFrozen && FROZEN_FORM_KEYS.has(formKey);

    const generateHeadline = () => {
        const title = isFresher ? "Fresher" : (jobtitle || "Professional");
        const course = getResolvedCourse() || "Education";
        const spec = getResolvedSpecialization() || "Specialization";
        const location = city || "desired location";

        return [
            `${title} with ${course} in ${spec} currently living in ${location}`,
            `Proactive ${title} specialized in ${spec} with a ${course} background, based in ${location}`,
            `${title} seeking to leverage ${course} and expertise in ${spec} in ${location}`
        ];
    };


    useEffect(() => {
        if (dataLoaded && isReviewMode && !initialEmploymentState) {
            setInitialEmploymentState({
                jobtitle: jobtitle || '',
                city: city || '',
                selectedDateFrom: selectedDateFrom || '',
                selectedDateTo: selectedDateTo || '',
                annualsal: annualsal || '',
                annualsal_scale: annualsal_scale || '',
                noticePeriod: noticePeriod || '',
                selectedOption: selectedOption || [],
                relevantExp: relevantExp || '',
                expectedCtc: expectedCtc || '',
                expectedCtcScale: expectedCtcScale || '',
                reasonJobChange: reasonJobChange || '',
                candidateQuery: candidateQuery || '',
                isSalaryNegotiable: isSalaryNegotiable || '',
                canNoticeBuyout: canNoticeBuyout || '',
                currentCompany: currentCompany || '',
                jobStatus: jobStatus || '',
                totalExperience: totalExperience,
                totalExpYears: totalExpYears,
                totalExpMonths: totalExpMonths,
                relevantExpYears: relevantExpYears,
                relevantExpMonths: relevantExpMonths,
                isFresher: isFresher
            });
        }
    }, [dataLoaded, isReviewMode, initialEmploymentState, jobtitle, city, selectedDateFrom, selectedDateTo, annualsal, annualsal_scale, noticePeriod, selectedOption, relevantExp, expectedCtc, expectedCtcScale, reasonJobChange, candidateQuery, isSalaryNegotiable, canNoticeBuyout, currentCompany, jobStatus, totalExperience, totalExpYears, totalExpMonths, relevantExpYears, relevantExpMonths, isFresher]);

    useEffect(() => {
        if (isFresher) {
            setRelevantExpError('');
            return;
        }
        setRelevantExpError(getTotalVsRelevantExpError(totalExpYears, totalExpMonths, relevantExpYears, relevantExpMonths));
    }, [isFresher, totalExpYears, totalExpMonths, relevantExpYears, relevantExpMonths]);

    useEffect(() => {
        if (!dataLoaded || !pendingHeadlineMatchRef.current) return;
        const saved = pendingHeadlineMatchRef.current;
        const matchedHeadlineIndex = generateHeadline().findIndex((headline) => headline === saved);
        if (matchedHeadlineIndex >= 0) {
            setSelectedHeadlineIndex(matchedHeadlineIndex);
            setIsChecked(true);
        }
        pendingHeadlineMatchRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLoaded, jobtitle, city, selectedCourses, selectedSpecializations, isFresher]);

    // ✅ Auto-fill only if checkbox is checked
    useEffect(() => {
        if (isChecked) {
            setResumeHeadline(generateHeadline()[selectedHeadlineIndex]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFresher, jobtitle, selectedCourses, selectedSpecializations, otherCourse, otherSpecialization, city, isChecked, selectedHeadlineIndex]);

    // ✅ Auto-select first headline suggestion when step 2 data loads
    useEffect(() => {
        if (!dataLoaded || isChecked) return;
        const hasSavedHeadline = Boolean(String(resumeHeadline || '').trim());
        if (!hasSavedHeadline) {
            handleHeadlineSelection(0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLoaded, isChecked, resumeHeadline]);

    // ✅ Textarea change
    const handleTextareaChange = (event) => {
        const value = event.target.value;
        setResumeHeadline(value);
        setValidationError(value.trim() === '' ? 'Resume headline is required' : '');
        if (value.trim()) logFieldToSentry('Resume headline', value);

        // If user edits manually → uncheck checkbox
        if (isChecked) {
            setIsChecked(false);
        }
    };

    // ✅ Selection change for multiple suggestions
    const handleHeadlineSelection = (index) => {
        setSelectedHeadlineIndex(index);
        setIsChecked(true); // Auto-check if a specific suggestion is picked
        setValidationError("");
        const headline = generateHeadline()[index];
        setResumeHeadline(headline);
        logFieldToSentry('Resume headline', headline);
    };

    const currentYear = new Date().getFullYear();
    const fiveYearsBefore = [];
    const fiveYearLater = [];

    for (let i = 0; i < 5; i++) {
        fiveYearsBefore.push(currentYear - i);
    }
    for (let i = 0; i < 5; i++) {
        fiveYearLater.push(currentYear + i);
    }

    // Helper: extract 4-digit year from "YYYY-MM" or plain "YYYY"/number
    const extractYear = (val) => {
        if (!val) return '';
        const str = String(val);
        if (str.includes('-')) return str.substring(0, 4);
        return str.substring(0, 4);
    };

    const handleNumberInputChange = (event) => {
        const val = event.target.value; // "YYYY-MM" from month input
        setNumberValue(val);
        validateEducationYears(val, passingYear, qualification);
        if (val) logFieldToSentry('Starting date', val);
    };

    const handlePassYearchange = (event) => {
        const val = event.target.value;
        setPassingYear(val);
        validateEducationYears(numberValue, val, qualification);
        if (val) logFieldToSentry('Passing date', val);
    }

    const getQualificationDuration = (qual) => {
        if (!qual) return { min: 1, max: 10 };
        const q = qual.toLowerCase();
        if (q.includes('doctorate') || q.includes('phd')) return { min: 3, max: 6 };
        if (q.includes('masters') || q.includes('post-graduation')) return { min: 1, max: 4 };
        if (q.includes('graduation')) return { min: 2, max: 6 };
        if (q.includes('diploma')) return { min: 1, max: 3 };
        if (q.includes('12th') || q.includes('intermediate')) return { min: 1, max: 2 };
        if (q.includes('10th') || q.includes('highschool')) return { min: 1, max: 2 };
        return { min: 1, max: 10 };
    };

    const getYearDiff = (startVal, endVal) => {
        const startDate = new Date(startVal);
        const endDate = new Date(endVal);
        // Exact difference in years (decimal)
        const diffMs = endDate - startDate;
        const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
        return parseFloat(diffYears.toFixed(2));
    };

    // ✅ qualification parameter add kiya
    const validateEducationYears = (startVal, endVal, currentQualification) => {
        const currentYear = new Date().getFullYear();
        if (startVal && endVal) {
            const startDate = new Date(startVal);
            const endDate = new Date(endVal);
            const end = parseInt(extractYear(endVal));

            const diff = getYearDiff(startVal, endVal); // ✅ exact diff
            const { min, max } = getQualificationDuration(currentQualification || qualification);

            if (startDate >= endDate) {
                setYearError('Passing date must be after the starting date.');
            } else if (end > currentYear) {
                setYearError(`Passing year cannot be greater than current year (${currentYear}).`);
            } else if (diff < min) {
                setYearError(`${currentQualification || qualification || 'This qualification'} duration must be at least ${min} year(s).`);
            } else if (diff > max) {
                setYearError(`${currentQualification || qualification || 'This qualification'} duration cannot exceed ${max} years.`);
            } else {
                setYearError('');
            }
        } else {
            setYearError('');
        }
    };

    const handleUniversityChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : '';
        setUniversity(val);
        if (val) logFieldToSentry('University / Institute', val);
    };

    const handleCourseChange = (selectedOption) => {
        setSelectedCourses(selectedOption);
        setIsCourseSelected(selectedOption !== null);
        if (selectedOption?.value !== 'Other') setOtherCourse('');
        // Reset specialization when course changes (isMulti status may change)
        setSelectedSpecializations(null);
        setAreSpecializationsSelected(false);
        setOtherSpecialization('');
        if (selectedOption?.value) logFieldToSentry('Course', selectedOption.value);
    };

    const handleSpecializationChange = (selectedOption) => {
        setSelectedSpecializations(selectedOption);
        const hasOther = Array.isArray(selectedOption)
            ? selectedOption.some((s) => s.value === 'Other')
            : selectedOption?.value === 'Other';
        if (!hasOther) setOtherSpecialization('');
        setAreSpecializationsSelected(
            Array.isArray(selectedOption) ? selectedOption.length > 0 : selectedOption !== null
        );
        if (selectedOption) {
            const specValue = Array.isArray(selectedOption)
                ? selectedOption.map((s) => s.value)
                : selectedOption.value;
            logFieldToSentry('Specialization', specValue);
        }
    };

    // Only MBA allows multiple specializations
    const isSpecializationMulti = () => {
        return selectedCourses?.value === 'MBA';
    };

    // Return relevant specializations based on selected course
    const getCourseSpecializations = () => getSpecializationsForCourse(selectedCourses?.value);

    // Returns available course types based on education vs work date overlap
    // Full Time only if education was fully completed before work started (exact date comparison)
    const getAvailableCourseTypes = () => {
        if (!selectedDateFrom || !numberValue || !passingYear) return courseType;
        const workStart = new Date(selectedDateFrom);
        const eduStart = new Date(numberValue);
        const eduEnd = new Date(passingYear);
        if (eduStart >= workStart || eduEnd >= workStart) {
            return ['Part Time', 'Distance Learning'];
        }
        return courseType;
    };

    const qualifications = [
        "Doctorate/PhD",
        "Masters/Post-Graduation",
        "Graduation",
        "Diploma",
        "Intermediate (12th)",
        "Highschool (10th)"
    ];
    const courseType = ["Full Time", "Part Time", "Distance Learning"];

    const [errors, setErrors] = useState({
        jobtitle: '',
        currentCompany: '',
        workStatus: '',
        city: '',
        working: '',
        annualsal: '',
        skills: '',
        notice: ''
    });

    const citySuggestion = ["Mumbai", "Chennai", "New Delhi", "Pune", "Delhi NCR", "Bangalore/Bengaluru"];
    const noticeArray = ["Immediate Joiner", "15 Days", "1 Month", "2 Months", "3 Months", "More than 3 Months"];
    const currentDate = new Date().toISOString().split('T')[0];

    const handleQualificationChange = (event) => {
        const selectedQualification = event.target.value;
        setQualification(selectedQualification);
        setIsQualificationSelected(selectedQualification !== '');
        // Reset course and specialization when qualification changes
        setSelectedCourses(null);
        setIsCourseSelected(false);
        setOtherCourse('');
        setSelectedSpecializations(null);
        setAreSpecializationsSelected(false);
        setOtherSpecialization('');
        if (selectedQualification) logFieldToSentry('Highest qualification', selectedQualification);

        // ✅ Dates already filled hain toh re-validate karo naye qualification se
        if (numberValue && passingYear) {
            validateEducationYears(numberValue, passingYear, selectedQualification);
        }
    };

    // Filter courses based on selected qualification
    const getFilteredCourses = () => {
        let filtered = Course;
        if (qualification) {
            const qualLower = qualification.toLowerCase();
            if (qualLower.includes('doctorate') || qualLower.includes('phd')) {
                filtered = Course.filter((c) => c.category === 'phd');
            } else if (qualLower.includes('masters') || qualLower.includes('post-graduation')) {
                filtered = Course.filter((c) => c.category === 'masters');
            } else if (qualLower.includes('graduation')) {
                filtered = Course.filter((c) => c.category === 'graduation');
            } else if (qualLower.includes('diploma')) {
                filtered = Course.filter((c) => c.category === 'diploma');
            } else if (qualLower.includes('12th') || qualLower.includes('intermediate')) {
                filtered = Course.filter((c) => c.category === '12th');
            } else if (qualLower.includes('10th') || qualLower.includes('highschool')) {
                filtered = Course.filter((c) => c.category === '10th');
            }
        }
        return appendOtherCourseOption(filtered);
    };

    const validateDates = (from, to) => {
        if (from && to) {
            const fromDateObj = new Date(from);
            const toDateObj = new Date(to);

            if (fromDateObj > toDateObj) {
                setDateError('From date cannot be after to date.');
            } else {
                setDateError('');
            }
        }
    };

    // InterviewSchedule
    const getExperience = (workingfrom, workingto) => {
        const from = new Date(workingfrom);
        const to = new Date(workingto);

        let years = to.getFullYear() - from.getFullYear();
        let months = to.getMonth() - from.getMonth();

        if (months < 0) {
            years -= 1;
            months += 12;
        }

        // Convert months into decimal part of a year
        const result = years + months / 10;
        return result;
    }

    const checkAlreadyExist = async () => {
        try {
            const refreshedToken = await user?.getIdToken();
            const data = JSON.stringify({
                "operation": "checkcandidate",
                "checkby": "email",
                "email": user?.email,
                "requisition_id": userData?.getReqId,
            });
            const headers = {
                'Authorization': `Bearer ${refreshedToken}`,
                'Content-Type': 'application/json'
            };
            // Make a POST request to the API endpoint
            const response = await axios.post(
                `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/check-candidate`,
                // `https://nmdev.in/api/v3.0/candidates/check-candidate`,
                data,
                { headers }
            );
            if (response.data?.isExist === "1") {
                return 1
            }
            else {
                return 0;
            }
        } catch (error) {
            logSentryException(error);
        }
    }

    const interviewSchedule = async (getCanExp, overrideData = {}) => {
        if (isSchedulingRef.current) return;
        isSchedulingRef.current = true;

        const getContactNum = userData?.contactNumber?.replace("+", "");
        // const mob = "+917838975692"
        // const getContactNum = mob?.replace("+", "");
        const resolvedReqId = userData?.getReqId || overrideData?.requisition_id;
        const resolvedContactNo = getContactNum || (profile?.userfetchedData?.contact_no ? String(profile.userfetchedData.contact_no).replace("+", "") : undefined)
            || (overrideData?.contact_no ? String(overrideData.contact_no).replace("+", "") : undefined);
        const resolvedResumeUrl = userData?.resumeUrl || profile?.userfetchedData?.resume_url || overrideData?.resume_url;
        const resolvedCurrentCompany = currentCompany || userData?.current_company || overrideData?.current_company;
        const resolvedJobStatus = jobStatus || userData?.job_status || overrideData?.job_status;
        const postData =
        {
            user_name: user?.displayName,
            email: user?.email,
            added_by_email: "sheela@virtualemployee.com",
            added_by_name: "Sheela AI",
            added_by_id: "",
            hrType: "1",
            contact_no: resolvedContactNo,
            operation: "addcandidate",
            resume_url: resolvedResumeUrl,
            request_id: resolvedReqId,
            requisition_id: resolvedReqId,
            referralCheck: 0,
            is_referral: 0,
            experience: getCanExp || profile?.userfetchedData?.total_exp || overrideData?.total_exp,
            // current_ctc: annualsal,
            current_ctc: annualsal_scale ? String(Math.round(parseFloat(annualsal) * SAL_SCALE_MULTIPLIERS[annualsal_scale])) : annualsal,
            technical_round: userData?.getInterviewtype
                ? (userData?.getInterviewtype === 'technical' ? 1 : 0)
                : (overrideData?.is_tech_done ?? 0),
            one2one_round: userData?.getInterviewtype
                ? ((userData?.getInterviewtype === 'non-technical' || userData?.getInterviewtype === 'technical') ? 1 : 0)
                : (overrideData?.is_onetoone_done ?? 0),
            comm_skill_rating: '',
            cultural_fit_rating: '',
            duration: '',
            audio_url: '',
            relevant_experience: relevantExp ? String(relevantExp) : "",
            relevant_exp: relevantExp ? String(relevantExp) : "",
            relavant_experience: relevantExp ? String(relevantExp) : "",
            expected_ctc: expectedCtcScale ? String(Math.round(parseFloat(expectedCtc) * SAL_SCALE_MULTIPLIERS[expectedCtcScale])) : expectedCtc,
            reason_job_change: reasonJobChange,
            candidate_ques: candidateQuery,
            salaryNegotiation: isSalaryNegotiable,
            is_notice_buyout: canNoticeBuyout,
            current_role: jobtitle,
            interview_date: minDate,
            interview_time: currentTime,

            notice_period: noticePeriod,
            current_company: resolvedCurrentCompany,
            source_of_candidate: new URLSearchParams(window.location.search).get('source') || userData?.source_of_candidate || profile?.userfetchedData?.source_of_candidate || "company_website",
            interview_medium: "NM Interview",
            job_status: resolvedJobStatus,
            city: city,
            skills: selectedOption?.map(obj => obj.value).join(', '),
        };
        const refreshedToken = await user?.getIdToken();
        const headers = {
            'Authorization': `Bearer ${refreshedToken}`
        };
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/add-candidate`,
                postData,
                { headers }
            );
            if (response.data?.statusCode === 200) {
                await addCandidateStatus(resolvedReqId);
                const getUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`
                const requestData = {
                    "email": user?.email
                }
                getInterviewdata(getUrl, requestData, refreshedToken)
            } else if (response.data?.statusCode === 429) {
                toast.error(response.data?.message || 'Too many interviews already scheduled. Cannot schedule more than 3 interviews within a one month period.');
            }
            // setScheduleData(response.data);

        } catch (error) {
            logSentryException(error);
            if (error.response?.status === 429) {
                toast.error(error.response?.data?.message || 'Too many interviews already scheduled. Cannot schedule more than 3 interviews within a one month period.');
            } else {
                console.error("Error scheduling interview:", error);
            }
        } finally {
            isSchedulingRef.current = false;
        }
    };

    // Unified once-per-session interview scheduling trigger
    const triggerInterviewScheduleOnce = async () => {
        if (isEditMode) return; // ❌ No scheduling in edit mode
        if (hasScheduledInterview.current) return;
        try {
            const getCanExp = (selectedDateFrom && selectedDateTo)
                ? getExperience(selectedDateFrom, selectedDateTo)
                : 0;

            if (userData?.getReqId) {
                const Isexist = await checkAlreadyExist();
                if (Isexist !== 1) {
                    await interviewSchedule(getCanExp);
                } else {
                    const refreshedToken = await user?.getIdToken();
                    const getUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
                    const requestData = { email: user?.email };
                    getInterviewdata(getUrl, requestData, refreshedToken);
                }
                hasScheduledInterview.current = true;
            } else {
                // Fallback: fetch reqId from fetchuser API
                try {
                    const refreshedToken = await user?.getIdToken();
                    const fetchHeaders = {
                        'Authorization': `Bearer ${refreshedToken}`,
                        'Content-Type': 'application/json',
                    };
                    const fetchUserResponse = await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
                        operation: 'fetchuser',
                        email: user?.email,
                    }, { headers: fetchHeaders });

                    const fetchUserData = fetchUserResponse.data?.[0];
                    const fetchedReqId = fetchUserData?.requisition_id;

                    if (fetchedReqId) {
                        const candidateUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
                        const candidateResponse = await axios.post(
                            candidateUrl,
                            { email: user?.email },
                            { headers: fetchHeaders }
                        );
                        const candidateList = candidateResponse.data?.candidate || [];
                        const alreadyScheduled = candidateList.some(
                            (c) => Number(c.requisition_id) === Number(fetchedReqId)
                        );
                        if (!alreadyScheduled) {
                            await interviewSchedule(getCanExp, fetchUserData);
                        } else {
                            const requestData = { email: user?.email };
                            getInterviewdata(candidateUrl, requestData, refreshedToken);
                        }
                    } else {
                        // The user might be already scheduled but has no reqId in fetchuser.
                        // getInterviewdata will fetch candidate details and generate sheela questions if they exist.
                        const candidateUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
                        const requestData = { email: user?.email };
                        getInterviewdata(candidateUrl, requestData, refreshedToken);
                    }
                    hasScheduledInterview.current = true;
                } catch (fallbackError) {
                    logSentryException(fallbackError);
                    console.error("Error in fallback interview scheduling:", fallbackError);
                }
            }
        } catch (error) {
            logSentryException(error);
            console.error("Error in triggerInterviewScheduleOnce:", error);
        }
    };

    const addCandidateStatus = async (getRequisitionId) => {
        try {
            const refreshedToken = await user?.getIdToken();
            let postData = {
                requisitionId: userData?.getReqId || getRequisitionId,
                name: user?.displayName,
                email: user?.email,
                type: "candidate",
                status: "Candidate Added",
                note: `${user?.displayName} has been added by Sheela AI on ${moment().format('YYYY-MM-DD')} for the requisition.`,
                addedBy: "Sheela AI",
                addedByEmail: "sheela@virtualemployee.com",
                userType: "1"
            };
            var headers = {
                'Authorization': `Bearer ${refreshedToken}`,
                'Content-Type': 'application/json'
            };
            const response = await axios.post(
                // `https://nmdev.in/api/v3.0/requisitions/add-status`,
                `${process.env.REACT_APP_UPLOAD}/api/v3.0/requisitions/add-status`,
                postData,
                { headers }
            )
            return response;
        } catch (error) {
            logSentryException(error);
            console.error("Error adding candidate status:", error);
            // throw error;
        }
    }

    const getInterviewdata = async (url, payload, getToken) => {
        if (isReviewMode) return
        const refreshedToken = getToken;
        const headers = {
            'Authorization': `Bearer ${refreshedToken}`,
            'Content-Type': 'application/json',
        };
        try {
            const response = await axios.post(url, payload, {
                headers: headers,
            });
            if (response) {
                const getData = response.data;
                if (getData && getData.candidate && getData.candidate.length > 0) {
                    const candidate = getData.candidate[0];
                    if (candidate.requisitionDetails) {
                        handleUserChange("getJobtitle", candidate.requisitionDetails.jobTitle);
                        handleUserChange("requisition_for", candidate.requisitionDetails.requisition_for);
                    }

                    // sheelaQuestionGenerate
                    const getRequisitionfor = candidate.requisitionDetails?.requisition_for;

                    // if (getData?.candidate?.[0]?.is_one2one === 1) {
                    if (getRequisitionfor === "avp") {
                        const questionUrl = `${process.env.REACT_APP_OPEN_API}/getSheelaRoundQuestions`
                        const questionRequest = {
                            "user_id": userData?.userId,
                            "req_id": candidate.requisition_id,
                            "userexperience": candidate.totalExp,
                        }
                        await axios.post(questionUrl, questionRequest, {
                            headers: headers,
                        });
                    }
                    // sheelaQuestionGenerate
                }
            }
            return response.data;
        } catch (error) {
            logSentryException(error);
            console.error("Error posting data:", error);
            // throw error; // rethrow so caller can handle it
        }
    };
    // ✅ Bulk save all Step 2 data (employment + education + headline) in one API call
    const handleSaveStep2 = async () => {
        setForm5Submit(true);
        // Collect ALL validation errors at once — no early returns, so every missing field is highlighted
        const newErrors = { jobtitle: '', currentCompany: '', workStatus: '', city: '', working: '', annualsal: '', skills: '', notice: '' };
        let hasError = false;

        if (!jobStatus || !WORK_STATUS_VALUES.includes(jobStatus)) {
            newErrors.workStatus = 'Please select your work status.';
            hasError = true;
        }

        if (!isFresher) {
            if (!jobtitle || !String(jobtitle).trim()) { newErrors.jobtitle = 'Please enter your job title.'; hasError = true; }
            if (isCompanyRequired && (!currentCompany || !String(currentCompany).trim())) {
                newErrors.currentCompany = isUnemployed
                    ? 'Please enter your last company.'
                    : 'Please enter your current company.';
                hasError = true;
            }
            if (!city) { newErrors.city = 'Please select your current city.'; hasError = true; }
            if (!selectedDateFrom || !selectedDateTo) { hasError = true; } // shown via date pickers below
            if (dateError) { hasError = true; }
            if (!annualsal || !annualsal_scale) { newErrors.annualsal = 'Please enter your annual salary.'; hasError = true; }
            if (totalExpYears === '' || totalExpMonths === '' || (totalExpYears === '0' && totalExpMonths === '0')) { hasError = true; } // shown via isform5Submitted
            const expRelationErr = getTotalVsRelevantExpError(totalExpYears, totalExpMonths, relevantExpYears, relevantExpMonths);
            if (expRelationErr) {
                setRelevantExpError(expRelationErr);
                hasError = true;
            }
        } else {
            if (!city) { newErrors.city = 'Please select your current city.'; hasError = true; }
        }
        if (!selectedOption || selectedOption.length === 0) { newErrors.skills = 'Please add at least one skill.'; hasError = true; }
        if (!qualification) { hasError = true; }          // shown via isform5Submitted
        if (!selectedCourses) { hasError = true; }        // shown via isform5Submitted
        else if (isCourseOtherSelected() && !otherCourse.trim()) { hasError = true; }
        if (!selectedSpecializations) { hasError = true; } // shown via isform5Submitted
        else if (isSpecializationOtherSelected() && !otherSpecialization.trim()) { hasError = true; }
        if (!university || !String(university).trim()) { hasError = true; } // shown via isform5Submitted
        if (university === 'Other' && !otherUniversity.trim()) { hasError = true; }
        if (!courseVal) { hasError = true; }              // shown via isform5Submitted
        if (!numberValue || !passingYear) { hasError = true; } // shown via isform5Submitted
        if (yearError) { hasError = true; }

        const headlineSuggestions = generateHeadline();
        const finalHeadline = resumeHeadline.trim()
            || (selectedHeadlineIndex !== null && isChecked ? headlineSuggestions[selectedHeadlineIndex] : '');
        if (!finalHeadline) { hasError = true; }

        setErrors(newErrors);
        if (hasError) { toast.error('Please fill all required fields highlighted above.'); return; }

        setLoading(true);
        try {
            const refreshedToken = await user?.getIdToken();
            const headers = { Authorization: `Bearer ${refreshedToken}`, 'Content-Type': 'application/json' };
            const finalUniversity = university === 'Other' ? otherUniversity.trim() : university;
            const commaSeparatedSkills = (selectedOption || []).map(obj => obj.value).join(', ');

            const payload = {
                login_token: user?.uid,
                user_email: user?.email,
                operation: 'saveBulkStep2',
                // Employment
                jobtitle: jobtitle || (isFresher ? 'Fresher' : ''),
                city,
                workingfrom: selectedDateFrom || '',
                workingto: selectedDateTo || '',
                annualsal: String(parseInt(annualsal_scale ? String(Math.round(parseFloat(annualsal) * SAL_SCALE_MULTIPLIERS[annualsal_scale])) : (annualsal || '0'), 10) || 0),
                noticeperiod: noticePeriod || '',
                skills: commaSeparatedSkills,
                current_company: currentCompany,
                total_exp: totalExpYears + '.' + totalExpMonths,
                job_status: jobStatus,
                candidate_status: isFresher ? 'fresher' : 'experienced',
                relavant_experience: relevantExpYears + '.' + relevantExpMonths,
                relevant_experience: relevantExpYears + '.' + relevantExpMonths,
                relevant_exp: relevantExpYears + '.' + relevantExpMonths,
                expected_ctc: String(parseInt(expectedCtcScale ? String(Math.round(parseFloat(expectedCtc) * SAL_SCALE_MULTIPLIERS[expectedCtcScale])) : (expectedCtc || '0'), 10) || 0),
                reason_job_change: reasonJobChange,
                salaryNegotiation: isSalaryNegotiable,
                is_notice_buyout: canNoticeBuyout,
                candidate_ques: candidateQuery,
                // Education
                higest_qualification: qualification,
                course: getResolvedCourse(),
                specialization: getResolvedSpecialization(),
                university: finalUniversity,
                course_type: courseVal,
                start_year: extractYear(numberValue),
                pass_year: extractYear(passingYear),
                // Headline
                resume_heading: finalHeadline,
                added_by_email: 'sheela@virtualemployee.com',
                added_by: 'Sheela AI',
                req_id: userData?.getReqId || '',
            };

            const url = `${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/saveBulkStep2`;
            const response = await axios.post(url, payload, { headers });
            if (response.data?.statusCode === 200 || response.data?.affectedRows > 0) {
                await triggerInterviewScheduleOnce();
                setIsCompleted(true);
                setCurrentStep(2);
                toast.success('Profile saved successfully!');
                sessionStorage.removeItem('revalidationMode');
                sessionStorage.removeItem('editMode');
                sessionStorage.removeItem('editStartStep');
                setLoading(false);
                navigate(isReviewMode ? '/account' : '/download', { state: { fromRegistration: true } });
            } else {
                setLoading(false);
                toast.error('Failed to save profile. Please try again.');
            }
        } catch (error) {
            logSentryException(error, { payload: { jobtitle, city, jobStatus, qualification } });
            setLoading(false);
            toast.error('An error occurred while saving. Please try again.');
            console.error(error);
        }
    };

    const handleFinish = async () => {
        try {
            const refreshedToken = await user?.getIdToken();
            await axios.post(
                `${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/touchProfile`,
                { login_token: user?.uid },
                { headers: { Authorization: `Bearer ${refreshedToken}`, 'Content-Type': 'application/json' } }
            );
        } catch (e) {
            logSentryException(e);
            console.error(e);
        }
        sessionStorage.removeItem('revalidationMode');
        sessionStorage.removeItem('editMode');
        sessionStorage.removeItem('editStartStep');
        navigate('/account');
    };

    const handleChange = (newAddress) => {
        setAddress(newAddress);
        setErrors((prevErrors) => ({
            ...prevErrors,
            city: '',
        }));
        if (newAddress?.trim()) logFieldToSentry('Current city', newAddress);
    };

    const getAnnualsalMaxLength = (scale) => {
        if (scale === 'Crore') return 2;
        if (scale === 'Lakhs') return 3;
        if (scale === 'Thousands') return 3;
        return 3;
    };

    const getAnnualsalFinalAmount = (value, scale) => {
        const num = parseFloat(value);
        if (!value || isNaN(num) || !scale) return null;
        const multipliers = SAL_SCALE_MULTIPLIERS;
        const multiplier = multipliers[scale];
        if (!multiplier) return null;
        return (num * multiplier).toLocaleString('en-IN');
    };

    const handleNumberChange = (event) => {
        let value = event.target.value;
        // allow only digits
        value = value.replace(/\D/g, '');
        const maxLen = getAnnualsalMaxLength(annualsal_scale);
        if (value.length > maxLen) return;
        setAnnualsal(value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            annualsal: '',
        }));
        if (value) logFieldToSentry('Annual salary', { amount: value, scale: annualsal_scale });
    };

    const handleAnnualsalScaleChange = (event) => {
        const newScale = event.target.value;
        setAnnualsalScale(newScale);
        // trim existing value if it exceeds new scale's limit
        const maxLen = getAnnualsalMaxLength(newScale);
        if (annualsal.length > maxLen) {
            setAnnualsal(annualsal.slice(0, maxLen));
        }
        setErrors((prevErrors) => ({
            ...prevErrors,
            annualsal: '',
        }));
        if (newScale) logFieldToSentry('Annual salary scale', { amount: annualsal, scale: newScale });
    };

    const handleDateChangeFrom = (event) => {
        setSelectedDateFrom(event.target.value);
        validateDates(event.target.value, selectedDateTo);
        if (event.target.value) logFieldToSentry('Working since', event.target.value);
    };

    const handleDateChangeTo = (event) => {
        setSelectedDateTo(event.target.value);
        validateDates(selectedDateFrom, event.target.value);
        if (event.target.value) logFieldToSentry('Working to', event.target.value);
    };

    const handleSelect = async (selectedAddress) => {
        setErrors((prevErrors) => ({
            ...prevErrors,
            city: '',
        }));
        setAddress(selectedAddress);

        try {
            const geocodeResults = await geocodeByAddress(selectedAddress);
            const { address_components } = geocodeResults[0];

            // Define priority for city/locality components
            const cityTypes = [
                'locality',
                'sublocality_level_1',
                'sublocality',
                'administrative_area_level_3',
                'administrative_area_level_2'
            ];

            let foundCity = '';
            for (const type of cityTypes) {
                const component = address_components.find(c => c.types.includes(type));
                if (component) {
                    foundCity = component.long_name;
                    break;
                }
            }

            // Fallback for Union Territories like Delhi
            if (!foundCity) {
                const adminArea1 = address_components.find(c => c.types.includes('administrative_area_level_1'));
                if (adminArea1) {
                    foundCity = adminArea1.long_name;
                }
            }

            // 1. Prioritize "New Delhi" if it's anywhere in the user's selected address or extracted city
            const fullAddressLower = (selectedAddress || '').toLowerCase();
            if (fullAddressLower.includes('new delhi')) {
                foundCity = 'New Delhi';
            } else if (fullAddressLower.includes('delhi')) {
                // 2. If it's Delhi (but not New Delhi), clean up "Division", "District" etc.
                if (foundCity.toLowerCase().includes('delhi')) {
                    foundCity = foundCity.replace(/\s+(Division|District|Taluk|Tehsil|Subdivision|Town|City)$/i, '').trim();
                } else if (foundCity === '') {
                    foundCity = 'Delhi';
                }
            }

            // 3. Generic cleanup for all other cities
            if (foundCity && !foundCity.toLowerCase().includes('new delhi')) {
                foundCity = foundCity.replace(/\s+(Division|District|Taluk|Tehsil|Subdivision|Town|City)$/i, '').trim();
            }

            // 4. Final fallback: Use the first part of the selection if still empty
            if (!foundCity && selectedAddress) {
                foundCity = selectedAddress.split(',')[0].trim();
            }

            // Ensure we don't end up with "Delhi Division" under any circumstances
            if (foundCity.toLowerCase() === 'delhi division') {
                foundCity = 'Delhi';
            }

            setCity(foundCity);
            if (foundCity) logFieldToSentry('Current city', foundCity);
        } catch (error) {
            logSentryException(error);
            console.error('Error while geocoding:', error);
        }
    };

    const renderSuggestion = ({ description }) => {
        const cityRegex = /([^,]+),\s([^,]+)/;
        const matches = description.match(cityRegex);
        const cityName = matches ? matches[1] : '';
        const state = matches ? matches[2] : '';

        return (
            <div>
                {cityName && <strong>{cityName}</strong>}
                {state && (
                    <>
                        {cityName && ', '}
                        <span>{state}</span>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="complete-profile text-start">
            {/* Step 2: Professional Details */}
            {currentStep === 2 && !dataLoaded && (
                <div className="text-center py-4">
                    <span className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </span>
                </div>
            )}
            {currentStep === 2 && dataLoaded && (
                <div className="steps">
                    <div className="p-3">
                        {/* Info banners */}
                        {isReviewMode && (
                            <div className="alert alert-info d-flex align-items-center gap-2 rounded-3 py-2 mb-3" role="alert">
                                <i className="bi bi-info-circle-fill"></i>
                                <span>Review your details below. Update any section or click <strong>Save Changes</strong> when done.</span>
                            </div>
                        )}
                        {!isReviewMode && (
                            <div className="alert alert-primary theme-gradient-bg-lighter rounded-3 py-2 mb-3">
                                ⚡ Almost there! Complete your details to unlock your interview download.
                            </div>
                        )}

                        {/* HR Freeze banner */}
                        {isHrFrozen && (
                            <div className="alert alert-warning d-flex align-items-start gap-2 rounded-3 py-2 mb-3" role="alert">
                                <i className="bi bi-lock-fill mt-1"></i>
                                <div>
                                    <div className="fw-medium">Some fields are locked by HR</div>
                                    <div className="small">These fields cannot be changed. To edit them, contact support.</div>
                                </div>
                            </div>
                        )}

                        {/* ===== CARD 1: Employment + Education + Headline ===== */}
                        <div className="card border-0 rounded-3 p-3 mb-3 shadow-sm">

                            <div className="fs-14 fw-semibold text-primary mb-3 d-flex align-items-center gap-2">
                                <img src={briefcase} alt="briefcase" style={{ height: '15px' }} />Employment
                            </div>

                            {/* Work Status */}
                            <div className="form-group mb-3">
                                <label className="form-label fw-medium">{isHrFrozen && <span className="me-1">🔒</span>}Work Status<RequiredMark /></label>
                                <div className="d-flex flex-wrap gap-3" style={isHrFrozen ? { pointerEvents: 'none', opacity: 0.85 } : {}}>
                                    {[
                                        { label: 'In Job', status: 'In Job' },
                                        { label: 'Currently On Notice', status: 'Currently On Notice' },
                                        { label: 'Unemployed', status: 'Unemployed' },
                                        { label: 'Fresher', status: 'Fresher' },
                                    ].map(({ label, status }) => (
                                        <div className="form-check" key={label}>
                                            <input className="form-check-input" type="radio" name="workStatusOptions"
                                                id={`workStatus_${label}`} autoComplete="off"
                                                checked={jobStatus === status}
                                                disabled={isFieldDisabled('jobStatus')}
                                                onChange={() => {
                                                    setJobStatus(status);
                                                    setErrors((prevErrors) => ({ ...prevErrors, workStatus: '' }));
                                                    logFieldToSentry('Work Status', status);
                                                }} />
                                            <label className="form-check-label" htmlFor={`workStatus_${label}`}>{label}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors?.workStatus && <span className="small text-danger">{errors.workStatus}</span>}
                            </div>

                            <hr className="my-3" />

                            {/* ===== Job Details — experienced only ===== */}
                            {!isFresher && (<>
                                <div className="fs-14 fw-semibold text-primary mb-3 d-flex align-items-center gap-2">
                                    <img src={briefcase} alt="briefcase" style={{ height: '15px' }} />Job Details
                                </div>

                                {/* Current Company + Job Title */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">
                                                {isHrFrozen && <span className="me-1">🔒</span>}
                                                {isUnemployed ? 'Last Company' : 'Current Company'}
                                                {isCompanyRequired && <RequiredMark />}
                                            </label>
                                            <input type="text" className="form-control" placeholder={isUnemployed ? 'Enter last company name' : 'Enter company name'} value={currentCompany}
                                                onChange={(e) => {
                                                    setCurrentCompany(e.target.value);
                                                    setErrors((prevErrors) => ({ ...prevErrors, currentCompany: '' }));
                                                    if (e.target.value.trim()) logFieldToSentry(isUnemployed ? 'Last Company' : 'Current Company', e.target.value);
                                                }}
                                                disabled={isFieldDisabled('currentCompany')} />
                                            {errors?.currentCompany ? <div className="small text-danger mt-1">{errors.currentCompany}</div> : null}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Job title<RequiredMark /></label>
                                            <input type="text" className="form-control" placeholder='Eg. Software Developer' value={jobtitle}
                                                onChange={(event) => {
                                                    setJobTitle(event.target.value);
                                                    setErrors((prevErrors) => ({ ...prevErrors, jobtitle: '' }));
                                                    if (event.target.value.trim()) logFieldToSentry('Job title', event.target.value);
                                                }}
                                                disabled={isFieldDisabled('jobtitle')} />
                                            {errors?.jobtitle ? <div className="small text-danger mt-1">{errors.jobtitle}</div> : null}
                                        </div>
                                    </div>
                                </div>

                                {/* Working since + Working to */}
                                <div className="wrapper mb-3">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Working since<RequiredMark /></label>
                                                <DatePickerWithMonthName className="form-control" value={selectedDateFrom} onChange={handleDateChangeFrom} max={currentDate} />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label className="form-label">Working to<RequiredMark /></label>
                                                <DatePickerWithMonthName className="form-control" value={selectedDateTo} onChange={handleDateChangeTo} max={currentDate} />
                                            </div>
                                        </div>
                                    </div>
                                    {dateError && <div className="small text-danger mt-1">{dateError}</div>}
                                </div>

                                {/* Notice period + Can Notice Period be Bought Out? */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Notice period<OptionalMark /></label>
                                            {noticePeriod !== "" ? (
                                                <div className="d-flex align-items-center">
                                                    <span className="bg-primary d-inline-flex align-items-center rounded-pill text-white px-3 py-2">
                                                        {noticePeriod}
                                                        {!isFieldDisabled('noticePeriod') && (
                                                            <button type="button" className="btn-close btn-close-white ms-2"
                                                                onClick={() => { setNoticePeriod(""); setErrors((prevErrors) => ({ ...prevErrors, notice: '' })); }}></button>
                                                        )}
                                                    </span>
                                                </div>
                                            ) : (
                                                <select className="form-select" value={noticePeriod}
                                                    onChange={(e) => { setNoticePeriod(e.target.value); setErrors((prevErrors) => ({ ...prevErrors, notice: '' })); }}
                                                    disabled={isFieldDisabled('noticePeriod')}>
                                                    <option value="">Select notice period</option>
                                                    {noticeArray.map((notice) => (<option key={notice} value={notice}>{notice}</option>))}
                                                </select>
                                            )}
                                            {errors?.notice ? <div className="small text-danger mt-1">{errors?.notice}</div> : null}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Can Notice Period be Bought Out?<OptionalMark /></label>
                                            <select className="form-select" value={canNoticeBuyout} onChange={e => setCanNoticeBuyout(e.target.value)} disabled={isFieldDisabled('canNoticeBuyout')}>
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Total Experience + Relevant Experience */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Total Experience<RequiredMark /></label>
                                            <div className="d-flex gap-2">
                                                <select className="form-select" value={totalExpYears}
                                                    onChange={(e) => {
                                                        setTotalExpYears(e.target.value);
                                                        setTotalExperience(parseFloat(e.target.value + '.' + totalExpMonths) || 0);
                                                        if (e.target.value !== '') logFieldToSentry('Total Experience', { years: e.target.value, months: totalExpMonths });
                                                    }}
                                                    disabled={isFieldDisabled('totalExperience')}>
                                                    <option value="">Years</option>
                                                    {[...Array(21)].map((_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                                                </select>
                                                <select className="form-select" value={totalExpMonths}
                                                    onChange={(e) => {
                                                        setTotalExpMonths(e.target.value);
                                                        setTotalExperience(parseFloat(totalExpYears + '.' + e.target.value) || 0);
                                                        if (e.target.value !== '') logFieldToSentry('Total Experience', { years: totalExpYears, months: e.target.value });
                                                    }}
                                                    disabled={isFieldDisabled('totalExperience')}>
                                                    <option value="">Months</option>
                                                    {[...Array(12)].map((_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                                                </select>
                                            </div>
                                            {isform5Submitted && (totalExpYears === '' || totalExpMonths === '' || (totalExpYears === '0' && totalExpMonths === '0')) && (
                                                <div className="small text-danger mt-1">Total experience cannot be 0 years and 0 months.</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Relevant Experience<OptionalMark /></label>
                                            <div className="d-flex gap-2">
                                                <select className="form-select" value={relevantExpYears}
                                                    onChange={e => { setRelevantExpYears(e.target.value); setRelevantExp(e.target.value + '.' + relevantExpMonths); setRelevantExpError(''); }}
                                                    disabled={isFieldDisabled('relevantExp')}>
                                                    <option value="">Years</option>
                                                    {[...Array(21)].map((_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                                                </select>
                                                <select className="form-select" value={relevantExpMonths}
                                                    onChange={e => { setRelevantExpMonths(e.target.value); setRelevantExp(relevantExpYears + '.' + e.target.value); setRelevantExpError(''); }}
                                                    disabled={isFieldDisabled('relevantExp')}>
                                                    <option value="">Months</option>
                                                    {[...Array(12)].map((_, i) => (<option key={i} value={String(i)}>{i}</option>))}
                                                </select>
                                            </div>
                                            {relevantExpError && <div className="small text-danger mt-1">{relevantExpError}</div>}
                                        </div>
                                    </div>
                                </div>

                                {/* Annual salary + Expected CTC */}
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Annual salary<RequiredMark /></label>
                                            <div className="d-flex gap-2">
                                                <input type="text" inputMode="numeric" className="form-control" value={annualsal} onChange={handleNumberChange}
                                                    placeholder="Amount" maxLength={getAnnualsalMaxLength(annualsal_scale)} required disabled={isFieldDisabled('annualsal')} />
                                                <select className="form-select" value={annualsal_scale} onChange={handleAnnualsalScaleChange} required disabled={isFieldDisabled('annualsal')}>
                                                    <option value="">Scale</option>
                                                    <option value="Thousands">Thousands</option>
                                                    <option value="Lakhs">Lakhs</option>
                                                    <option value="Crore">Crore</option>
                                                </select>
                                            </div>
                                            {errors?.annualsal ? <div className="small text-danger mt-1">{errors?.annualsal}</div> : null}
                                            {getAnnualsalFinalAmount(annualsal, annualsal_scale) && (
                                                <div className="small text-muted mt-1">Final amount: &#8377;{getAnnualsalFinalAmount(annualsal, annualsal_scale)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Expected CTC<OptionalMark /></label>
                                            <div className="d-flex gap-2">
                                                <input type="text" inputMode="numeric" className="form-control" placeholder="Amount" value={expectedCtc}
                                                    maxLength={getAnnualsalMaxLength(expectedCtcScale)}
                                                    onChange={e => { const v = e.target.value.replace(/\D/g, ''); const max = getAnnualsalMaxLength(expectedCtcScale); if (v.length <= max) setExpectedCtc(v); }}
                                                    disabled={isFieldDisabled('expectedCtc')} />
                                                <select className="form-select" value={expectedCtcScale}
                                                    onChange={e => { setExpectedCtcScale(e.target.value); const max = getAnnualsalMaxLength(e.target.value); if (expectedCtc.length > max) setExpectedCtc(expectedCtc.slice(0, max)); }}
                                                    disabled={isFieldDisabled('expectedCtc')}>
                                                    <option value="">Scale</option>
                                                    <option value="Thousands">Thousands</option>
                                                    <option value="Lakhs">Lakhs</option>
                                                    <option value="Crore">Crore</option>
                                                </select>
                                            </div>
                                            {getAnnualsalFinalAmount(expectedCtc, expectedCtcScale) && (
                                                <div className="small text-muted mt-1">Final: &#8377;{getAnnualsalFinalAmount(expectedCtc, expectedCtcScale)}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Is Salary Negotiable? — left half */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Is Salary Negotiable?<OptionalMark /></label>
                                            <select className="form-select" value={isSalaryNegotiable} onChange={e => setIsSalaryNegotiable(e.target.value)} disabled={isFieldDisabled('isSalaryNegotiable')}>
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Reason for Job Change */}
                                <div className="form-group mb-3">
                                    <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Reason for Job Change<OptionalMark /></label>
                                    <textarea className="form-control" rows={2} placeholder="Eg. Better growth opportunities"
                                        value={reasonJobChange} onChange={e => setReasonJobChange(e.target.value)} disabled={isFieldDisabled('reasonJobChange')} />
                                </div>
                            </>)}

                            {/* Key Skills — full width, both modes */}
                            <div className="form-group mb-3">
                                <label className="form-label fw-medium">Key skills<RequiredMark /></label>
                                <CreatableSelect isMulti className="custom-select" classNamePrefix="custom-select" required closeMenuOnSelect={false}
                                    name="skills" options={SkillsVal} value={selectedOption} isDisabled={isFieldDisabled('skills')}
                                    onChange={(value) => {
                                        setSelectedOption(value);
                                        setErrors((prevErrors) => ({ ...prevErrors, skills: '' }));
                                        if (value?.length) logFieldToSentry('Key skills', value.map((v) => v.value));
                                    }}
                                    placeholder="Add your skills" />
                                <div className="form-text">Key skills are crucial for recruiters to hire you</div>
                                <div className="suggestion-wrapper mt-2">
                                    {errors?.skills ? <div className="small text-danger mb-2">{errors?.skills}</div> : null}
                                    <div className="suggestion-label small text-secondary mb-2">Suggested Skills:</div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {['HTML', 'CSS', 'JavaScript', 'React', 'Node.js', 'Python'].map((skill) => (
                                            <span key={skill} type="button" className="border pointer rounded-pill small text-secondary px-3 py-1"
                                                onClick={() => {
                                                    if (isFieldDisabled('skills')) return;
                                                    const newSkill = { value: skill, label: skill };
                                                    const currentSkills = selectedOption || [];
                                                    if (!currentSkills.find(s => s.value === skill)) {
                                                        const updated = [...currentSkills, newSkill];
                                                        setSelectedOption(updated);
                                                        logFieldToSentry('Key skills', updated.map((s) => s.value));
                                                    }
                                                }}>
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Current City — full width, both modes */}
                            <div className="form-group mb-3">
                                <label className="form-label fw-medium">Current city<RequiredMark /></label>
                                <div>
                                    {city !== "" ? (
                                        <div className="d-flex align-items-center">
                                            <span className="bg-primary d-inline-flex align-items-center rounded-pill text-white px-3 py-2">
                                                {city}
                                                {!isFieldDisabled('city') && (
                                                    <button type="button" className="btn-close btn-close-white ms-2"
                                                        onClick={() => { setCity(""); setAddress(""); setErrors((prevErrors) => ({ ...prevErrors, city: '' })); }}></button>
                                                )}
                                            </span>
                                        </div>
                                    ) : (
                                        <PlacesAutocomplete value={address} onChange={handleChange} onSelect={handleSelect} searchOptions={{ types: ['(cities)'] }}>
                                            {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                                <div className="position-relative">
                                                    <input {...getInputProps({ placeholder: 'Search Cities...' })} className="form-control" disabled={isFieldDisabled('city')} />
                                                    {suggestions.length > 0 && (
                                                        <div className="position-absolute bg-white border rounded-3 shadow top-100 w-100 py-2 z-1">
                                                            {suggestions.map((suggestion) => (
                                                                <div key={suggestion.id} {...getSuggestionItemProps(suggestion)} className="dropdown-item pointer" style={{ padding: '4px 12px' }}>
                                                                    {renderSuggestion(suggestion)}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </PlacesAutocomplete>
                                    )}
                                    {errors?.city ? <div className="small text-danger mt-1">{errors?.city}</div> : null}
                                </div>
                                <div className="suggestion-wrapper mt-2">
                                    <div className="suggestion-label small text-secondary mb-2">Suggested Cities:</div>
                                    <div className="d-flex flex-wrap gap-2 text-capitalize">
                                        {citySuggestion?.map((cityVal, index) => (
                                            <span key={index} type="button" className="border pointer rounded-pill small text-secondary px-3 py-1"
                                                onClick={() => {
                                                    if (isFieldDisabled('city')) return;
                                                    setCity(cityVal);
                                                    setAddress("");
                                                    setErrors((prevErrors) => ({ ...prevErrors, city: '' }));
                                                    logFieldToSentry('Current city', cityVal);
                                                }}>{cityVal}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Fresher-specific fields */}
                            {isFresher && (<>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Expected CTC<OptionalMark /></label>
                                            <div className="d-flex gap-2">
                                                <input type="text" inputMode="numeric" className="form-control" placeholder="Amount" value={expectedCtc}
                                                    maxLength={getAnnualsalMaxLength(expectedCtcScale)}
                                                    onChange={e => { const v = e.target.value.replace(/\D/g, ''); const max = getAnnualsalMaxLength(expectedCtcScale); if (v.length <= max) setExpectedCtc(v); }}
                                                    disabled={isFieldDisabled('expectedCtc')} />
                                                <select className="form-select" value={expectedCtcScale}
                                                    onChange={e => { setExpectedCtcScale(e.target.value); const max = getAnnualsalMaxLength(e.target.value); if (expectedCtc.length > max) setExpectedCtc(expectedCtc.slice(0, max)); }}
                                                    disabled={isFieldDisabled('expectedCtc')}>
                                                    <option value="">Scale</option>
                                                    <option value="Thousands">Thousands</option>
                                                    <option value="Lakhs">Lakhs</option>
                                                    <option value="Crore">Crore</option>
                                                </select>
                                            </div>
                                            {getAnnualsalFinalAmount(expectedCtc, expectedCtcScale) && (
                                                <div className="small text-muted mt-1">Final: &#8377;{getAnnualsalFinalAmount(expectedCtc, expectedCtcScale)}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="form-label">{isHrFrozen && <span className="me-1">🔒</span>}Is Salary Negotiable?<OptionalMark /></label>
                                            <select className="form-select" value={isSalaryNegotiable} onChange={e => setIsSalaryNegotiable(e.target.value)} disabled={isFieldDisabled('isSalaryNegotiable')}>
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </>)}

                            <hr className="my-3" />

                            {/* ===== Education ===== */}
                            <div className="fs-14 fw-semibold text-primary mb-3 d-flex align-items-center gap-2">
                                <img src={schoolbook} alt="schoolbook" style={{ height: '15px' }} />Education
                            </div>

                            <Row className="gx-3 gy-2 mb-2">
                                <Col md={6}>
                                    <div className="form-group">
                                        <label className="form-label">Highest qualification<RequiredMark /></label>
                                        <select name="qualification" id="qualification" className="form-select" value={qualification} onChange={handleQualificationChange} required>
                                            <option value="" disabled>Choose your qualification</option>
                                            {qualifications.map((qual, index) => (<option key={index} value={qual}>{qual}</option>))}
                                        </select>
                                        <div className={`mt-1 ${isQualificationSelected ? 'small text-success' : 'small text-danger'}`}>
                                            {isform5Submitted && !isQualificationSelected ? 'Please select a qualification.' : ''}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label className="form-label">Course<RequiredMark /></label>
                                        {isHrFrozenEducationField('course') ? (
                                            <input type="text" className="form-control" readOnly value={getResolvedCourse()} />
                                        ) : (
                                            <div className={`d-flex gap-2 ${isCourseOtherSelected() ? '' : ''}`}>
                                                <div className={isCourseOtherSelected() ? 'w-50' : 'w-100'}>
                                                    <Select className="custom-select" classNamePrefix="custom-select" required closeMenuOnSelect
                                                        name="course" options={getFilteredCourses()} value={selectedCourses} onChange={handleCourseChange}
                                                        placeholder="Select your course" isClearable={false} />
                                                </div>
                                                {isCourseOtherSelected() && (
                                                    <input type="text" className="form-control flex-grow-1" placeholder="Enter your course name"
                                                        value={otherCourse} onChange={(e) => {
                                                            setOtherCourse(e.target.value);
                                                            if (e.target.value.trim()) logFieldToSentry('Other Course', e.target.value);
                                                        }} required />
                                                )}
                                            </div>
                                        )}
                                        <div className={`mt-1 ${isCourseSelected && (!isCourseOtherSelected() || otherCourse.trim()) ? 'small text-success' : 'small text-danger'}`}>
                                            {isform5Submitted && !isCourseSelected ? 'Please select a course.' : ''}
                                            {isform5Submitted && isCourseSelected && isCourseOtherSelected() && !otherCourse.trim() ? 'Please enter your course name.' : ''}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label className="form-label">
                                            Specialization<RequiredMark />
                                            {isSpecializationMulti() && (<span className="ms-2 small text-muted fw-normal">(Multiple allowed)</span>)}
                                        </label>
                                        {isHrFrozenEducationField('specialization') ? (
                                            <input type="text" className="form-control" readOnly value={getResolvedSpecialization()} />
                                        ) : (
                                            <div className="d-flex gap-2">
                                                <div className={isSpecializationOtherSelected() ? 'w-50' : 'w-100'}>
                                                    <Select className='custom-select' classNamePrefix='custom-select' required
                                                        isMulti={isSpecializationMulti()} closeMenuOnSelect={!isSpecializationMulti()}
                                                        name="specialization" options={getCourseSpecializations()} placeholder="Select your specialization"
                                                        value={selectedSpecializations} onChange={handleSpecializationChange} isClearable={false} />
                                                </div>
                                                {isSpecializationOtherSelected() && (
                                                    <input type="text" className="form-control flex-grow-1" placeholder="Enter your specialization"
                                                        value={otherSpecialization} onChange={(e) => {
                                                            setOtherSpecialization(e.target.value);
                                                            if (e.target.value.trim()) logFieldToSentry('Other Specialization', e.target.value);
                                                        }} required />
                                                )}
                                            </div>
                                        )}
                                        <div className={`mt-1 ${areSpecializationsSelected && (!isSpecializationOtherSelected() || otherSpecialization.trim()) ? 'small text-success' : 'small text-danger'}`}>
                                            {isform5Submitted && !areSpecializationsSelected ? 'Please select at least one specialization.' : ''}
                                            {isform5Submitted && areSpecializationsSelected && isSpecializationOtherSelected() && !otherSpecialization.trim() ? 'Please enter your specialization.' : ''}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group flex-fill">
                                        <label className="form-label">{qualification === 'Highschool (10th)' || qualification === 'Intermediate (12th)' ? 'Board / Institute' : 'University / Institute'}<RequiredMark /></label>
                                        <CreatableSelect className="custom-select" classNamePrefix="custom-select" required closeMenuOnSelect={true}
                                            name="university"
                                            options={qualification === 'Highschool (10th)' ? CommonBoards10th : (qualification === 'Intermediate (12th)' ? CommonBoards12th : CommonUniversities)}
                                            value={university ? { label: university, value: university } : null}
                                            onChange={handleUniversityChange}
                                            placeholder={qualification === 'Highschool (10th)' || qualification === 'Intermediate (12th)' ? "Eg. CBSE, State Board" : "Eg. National Institute of Technology (NIT)"} />
                                        {university === 'Other' && (
                                            <input type="text" className="form-control mt-2" placeholder="Enter your university/institute name"
                                                value={otherUniversity} onChange={(e) => {
                                                    setOtherUniversity(e.target.value);
                                                    if (e.target.value.trim()) logFieldToSentry('Other University / Institute', e.target.value);
                                                }} required />
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <Row className="gx-3 gy-2 mb-3">
                                <Col md={6}>
                                    <div className="form-group">
                                        <label className="form-label">Starting date<RequiredMark /></label>
                                        <DatePickerWithMonthName
                                            className={`form-control ${yearError && numberValue ? 'border-danger' : ''}`}
                                            value={numberValue} max={currentDate} placeholder="Select starting date"
                                            onChange={handleNumberInputChange} required />
                                        {isform5Submitted && !numberValue ? <div className="small text-danger mt-1">Please select starting date.</div> : null}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label className="form-label">Passing date<RequiredMark /></label>
                                        <DatePickerWithMonthName
                                            className={`form-control ${yearError && passingYear ? 'border-danger' : ''}`}
                                            value={passingYear}
                                            min={numberValue ? (() => { const d = new Date(numberValue); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })() : ''}
                                            max={currentDate} placeholder="Select passing date"
                                            onChange={handlePassYearchange} required />
                                        {isform5Submitted && !passingYear ? <div className="small text-danger mt-1">Please select passing date.</div> : null}
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-group mb-3">
                                <label className="form-label">Course type<RequiredMark /></label>
                                {(() => {
                                    const available = getAvailableCourseTypes();
                                    if (courseVal === 'Full Time' && !available.includes('Full Time')) { setCourse(''); setIsValidCourse(false); }
                                    return null;
                                })()}
                                <div className="radio-wrapper d-flex flex-wrap gap-2">
                                    {courseVal !== "" ? (
                                        <label className="bg-primary d-inline-flex align-items-center rounded-pill text-white px-3 py-2">{courseVal}
                                            <span className='ms-2 pointer' type="button" onClick={() => { setCourse(""); setIsValidCourse(false); }}><i className="bi bi-x-lg"></i></span>
                                        </label>
                                    ) : (
                                        getAvailableCourseTypes().map((course) => (
                                            <div className="radio-item text-nowrap" key={course}>
                                                <label className="border pointer rounded-pill small text-secondary px-3 py-1"
                                                    onClick={() => {
                                                        setCourse(course);
                                                        setIsValidCourse(true);
                                                        logFieldToSentry('Course type', course);
                                                    }}>{course}</label>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {!getAvailableCourseTypes().includes('Full Time') && selectedDateFrom && numberValue && passingYear && (
                                    <div className="small text-warning mt-1">
                                        <i className="bi bi-info-circle me-1"></i>Full Time not available — education period overlaps with employment.
                                    </div>
                                )}
                                {isform5Submitted && !isCourseValid ? <div className="small text-danger mt-1">please select Course.</div> : null}
                            </div>

                            {yearError && (
                                <div className="alert alert-danger rounded-3 py-2" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>{yearError}
                                </div>
                            )}

                            <hr className="my-3" />

                            <div className="fs-14 fw-semibold text-primary mb-3 d-flex align-items-center gap-2">
                                <i className="bi bi-card-text"></i>Resume Headline
                            </div>
                            {/* Resume Headline Preview Bar */}
                            <div className="d-flex align-items-center justify-content-between gap-2 bg-light border rounded-3 px-3 py-2 mb-3">
                                <span className="fs-14 text-dark text-truncate flex-grow-1" style={{ maxWidth: '80%' }}>
                                    {resumeHeadline || <span className="text-muted">Add a profile headline...</span>}
                                </span>
                                <Button variant="outline-secondary" size="sm" className="flex-shrink-0"
                                    onClick={() => setShowHeadlineModal(!showHeadlineModal)}>
                                    Change
                                </Button>
                            </div>

                            {/* Final CTA */}
                            <div className="btn-wrapper text-center mt-2 mb-2">
                                {(() => {
                                    const expRelationInvalid = !isFresher && !!getTotalVsRelevantExpError(
                                        totalExpYears, totalExpMonths, relevantExpYears, relevantExpMonths
                                    );
                                    const companyRequiredInvalid = isCompanyRequired
                                        && (!currentCompany || !String(currentCompany).trim());
                                    const headlineSuggestions = generateHeadline();
                                    const finalHeadline = resumeHeadline.trim()
                                        || (selectedHeadlineIndex !== null && isChecked ? headlineSuggestions[selectedHeadlineIndex] : '');

                                    let isStep2Complete = true;
                                    if (!jobStatus || !WORK_STATUS_VALUES.includes(jobStatus)) isStep2Complete = false;
                                    if (!isFresher) {
                                        if (!jobtitle || !String(jobtitle).trim()) isStep2Complete = false;
                                        if (!city) isStep2Complete = false;
                                        if (!selectedDateFrom || !selectedDateTo) isStep2Complete = false;
                                        if (dateError) isStep2Complete = false;
                                        if (!annualsal || !annualsal_scale) isStep2Complete = false;
                                        if (totalExpYears === '' || totalExpMonths === '' || (totalExpYears === '0' && totalExpMonths === '0')) isStep2Complete = false;
                                        if (expRelationInvalid) isStep2Complete = false;
                                        if (companyRequiredInvalid) isStep2Complete = false;
                                    } else if (!city) {
                                        isStep2Complete = false;
                                    }
                                    if (!selectedOption || selectedOption.length === 0) isStep2Complete = false;
                                    if (!qualification) isStep2Complete = false;
                                    if (!selectedCourses) isStep2Complete = false;
                                    else if (isCourseOtherSelected() && !otherCourse.trim()) isStep2Complete = false;
                                    if (!selectedSpecializations) isStep2Complete = false;
                                    else if (isSpecializationOtherSelected() && !otherSpecialization.trim()) isStep2Complete = false;
                                    if (!university || !String(university).trim()) isStep2Complete = false;
                                    if (university === 'Other' && !otherUniversity.trim()) isStep2Complete = false;
                                    if (!courseVal) isStep2Complete = false;
                                    if (!numberValue || !passingYear) isStep2Complete = false;
                                    if (yearError) isStep2Complete = false;
                                    if (!finalHeadline) isStep2Complete = false;

                                    let canSave = isStep2Complete;
                                    if (isReviewMode) {
                                        const initEmp = initialEmploymentState;
                                        const initEdu = initialEducationRef.current;
                                        const initHl = initialHeadlineRef.current;
                                        const snapshotsReady = Boolean(initEmp && initEdu && initHl);
                                        const hasFormChanged = snapshotsReady && (
                                            jobtitle !== initEmp.jobtitle ||
                                            city !== initEmp.city ||
                                            selectedDateFrom !== initEmp.selectedDateFrom ||
                                            selectedDateTo !== initEmp.selectedDateTo ||
                                            annualsal !== initEmp.annualsal ||
                                            annualsal_scale !== initEmp.annualsal_scale ||
                                            noticePeriod !== initEmp.noticePeriod ||
                                            JSON.stringify(selectedOption) !== JSON.stringify(initEmp.selectedOption) ||
                                            relevantExp !== initEmp.relevantExp ||
                                            expectedCtc !== initEmp.expectedCtc ||
                                            expectedCtcScale !== initEmp.expectedCtcScale ||
                                            reasonJobChange !== initEmp.reasonJobChange ||
                                            candidateQuery !== initEmp.candidateQuery ||
                                            isSalaryNegotiable !== initEmp.isSalaryNegotiable ||
                                            canNoticeBuyout !== initEmp.canNoticeBuyout ||
                                            currentCompany !== initEmp.currentCompany ||
                                            jobStatus !== initEmp.jobStatus ||
                                            totalExpYears !== initEmp.totalExpYears ||
                                            totalExpMonths !== initEmp.totalExpMonths ||
                                            relevantExpYears !== initEmp.relevantExpYears ||
                                            relevantExpMonths !== initEmp.relevantExpMonths ||
                                            isFresher !== initEmp.isFresher ||
                                            qualification !== initEdu.qualification ||
                                            (selectedCourses?.value || '') !== (initEdu.selectedCourses?.value || '') ||
                                            otherCourse !== (initEdu.otherCourse || '') ||
                                            JSON.stringify(selectedSpecializations) !== JSON.stringify(initEdu.selectedSpecializations) ||
                                            otherSpecialization !== (initEdu.otherSpecialization || '') ||
                                            university !== initEdu.university ||
                                            courseVal !== initEdu.courseVal ||
                                            numberValue !== initEdu.numberValue ||
                                            passingYear !== initEdu.passingYear ||
                                            finalHeadline !== (initHl.resumeHeadline || '').trim()
                                        );
                                        // Review/edit: require both a real change and a fully valid form.
                                        canSave = hasFormChanged && isStep2Complete;
                                    }
                                    return (
                                        <div className="d-flex gap-2">
                                            <Button variant='primary' className="btn-custom flex-grow-1 d-inline-flex align-items-center justify-content-center gap-2"
                                                onClick={handleSaveStep2} disabled={isLoading || !canSave}>
                                                {isReviewMode ? 'Save & Update Profile' : '🎉 Save & Unlock My Interview'}
                                                {isLoading && (
                                                    <span className="spinner-border spinner-border-sm text-light" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </span>
                                                )}
                                            </Button>
                                            {isReviewMode && (
                                                <Button variant='outline-primary' className="btn-custom d-inline-flex align-items-center justify-content-center gap-1"
                                                    onClick={handleFinish} disabled={isLoading}>
                                                    Finish <i className="bi bi-check-lg"></i>
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* ===== MODAL: AI Headline Suggestions ===== */}
                        <Modal show={showHeadlineModal} onHide={() => setShowHeadlineModal(false)} centered>
                            <Modal.Header closeButton>
                                <Modal.Title className="d-flex align-items-center gap-2">
                                    <span className="badge rounded-pill px-3 py-2 text-white" style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', fontSize: '12px' }}>AI Generated</span>
                                    <span className="fs-14 text-muted fw-normal">Suggestions based on your profile</span>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="d-flex flex-column gap-2 mb-3">
                                    {generateHeadline().map((headline, index) => (
                                        <div key={index}
                                            className={`suggestion-item bg-primary bg-opacity-10 border-primary rounded-3 px-3 py-2 transition-all ${selectedHeadlineIndex === index && isChecked ? 'border-opacity' : 'border-opacity-25'}`}
                                            style={{ border: '2px dashed', cursor: 'pointer' }}
                                            onClick={() => handleHeadlineSelection(index)}>
                                            <div className="form-check mb-0">
                                                <input className="form-check-input border-secondary border-opacity-75" type="radio" name="headlineSuggestion"
                                                    id={`headline-${index}`} checked={selectedHeadlineIndex === index && isChecked}
                                                    onChange={() => handleHeadlineSelection(index)} />
                                                <label htmlFor={`headline-${index}`} className="form-check-label fs-6 text-dark pointer-event" style={{ cursor: 'pointer' }}>
                                                    {headline}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="form-check form-switch mb-3">
                                    <input className="form-check-input" type="checkbox" role="switch" id="useSelectedHeadline"
                                        checked={isChecked} onChange={e => setIsChecked(e.target.checked)} />
                                    <label className="form-check-label small text-muted" htmlFor="useSelectedHeadline">Use selected suggestion</label>
                                </div>
                                <div className="form-group">
                                    <label className="form-label fw-medium">Resume Headline<RequiredMark /></label>
                                    <textarea className="form-control" id="textarea" rows="3" value={resumeHeadline} onChange={handleTextareaChange}
                                        placeholder='Add resume headline and preferences to make your profile stronger...' required></textarea>
                                    {validationError && <div className="small text-danger mt-1">{validationError}</div>}
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant='primary' size="sm" className="btn-custom d-inline-flex align-items-center justify-content-center gap-2"
                                    onClick={() => setShowHeadlineModal(false)}>
                                    Save <i className="bi bi-check2"></i>
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} theme="colored" />
        </div>
    )
}
